import { useState, useEffect, useRef, useMemo, useCallback, Component, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ZoomIn } from 'lucide-react';
import supabase from './supabaseClient';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { ZenLogo } from './landing/ZenLogo';
import DemoChatView from './demo/DemoChatView';
import PlanSelectPage from './onboarding/PlanSelectPage';
import ProfileSetupPage from './onboarding/ProfileSetupPage';
import { RACKET_SPORTS, browserTimezone, listTimezones, browserLocale, listLocales, localeLabel } from './utils/profileOptions';
import { todayLocal, setDisplayLocale, getDisplayLocale, formatDateLong as formatDateLocaleAware } from './utils/dateLocal';
import { COUNTRIES, countryDefaults } from './utils/countryData';
import { trackEvent } from './analytics';
import CookieBanner, { initGAIfConsented } from './components/CookieBanner';
import DataCoachApp from './data_coach/DataCoachApp';
import InstallPWA from './components/InstallPWA';
import TutorialOverlay from './components/TutorialOverlay';
import GamePlanTab, { SessionModal } from './components/GamePlanTab';
import GamePlanView from './components/GamePlanView';
import HomeView from './components/HomeView';
import { ConfirmDialogHost } from './components/ConfirmDialog';
import './App.css';

// Route-only, never needed by the authenticated app shell itself — lazy so
// their code isn't in every logged-in user's initial bundle. ZenLogo (used
// in the app's own header) was pulled out of LandingPage.js into its own
// tiny file specifically so this split was possible (bundle-size pass,
// 2026-10-21 — see production readiness audit).
const LandingPage = lazy(() => import('./landing/LandingPage'));
const RegisterPage = lazy(() => import('./landing/RegisterPage'));
const TermsPage = lazy(() => import('./landing/TermsPage'));
const PrivacyPage = lazy(() => import('./landing/PrivacyPage'));
const AboutPage = lazy(() => import('./landing/AboutPage'));
const ContactPage = lazy(() => import('./landing/ContactPage'));
const SportPage = lazy(() => import('./landing/SportPage'));
const AdminApp = lazy(() => import('./admin/AdminApp'));
// Minimal, theme-matched fallback — shown only for the brief moment a
// lazy chunk is downloading, not a full loading-state redesign.
function RouteFallback() {
  return <div style={{ minHeight: '100vh', background: '#060d1a' }} />;
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const triggerGoogleSignIn = async () => {
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });
};

// ── Error Boundary — catches render crashes and shows a diagnostic message ─────
// Exported (not just used internally) so index.js can wrap the whole app
// shell with it too — previously only each tab body was protected, so a
// crash in the nav/header/initial-auth-load path (outside any tab) took
// the entire page blank with nothing to recover from (2026-10-20
// production readiness audit).
export class TabErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error('[TabErrorBoundary]', e, info); }
  render() {
    if (this.state.error) {
      // fullPage: used at the top of index.js, outside any tab — a crash
      // here means the whole shell (nav, auth, routing) failed, so "Try
      // again" (just clearing local state) is unlikely to recover; a hard
      // reload is the one that actually works for a corrupted-state crash.
      const fullPage = this.props.fullPage;
      return (
        <div style={{
          padding: 32, margin: fullPage ? 0 : 16, background: '#161b22',
          borderRadius: fullPage ? 0 : 12, border: fullPage ? 'none' : '1px solid #ef444450',
          color: '#e6edf3', fontFamily: 'Inter, sans-serif',
          ...(fullPage ? { minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' } : {}),
        }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {fullPage ? 'ZenAnalytics hit a snag' : 'Something went wrong'}
          </div>
          <div style={{ fontSize: 13, color: '#8b949e', fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: fullPage ? 480 : undefined }}>
            {this.state.error?.message || String(this.state.error)}
          </div>
          <button
            onClick={() => fullPage ? window.location.reload() : this.setState({ error: null })}
            style={{
              marginTop: 16, padding: '8px 16px', background: '#10b981',
              border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer',
              fontFamily: 'Inter, sans-serif', fontWeight: 600,
            }}>{fullPage ? 'Reload page' : 'Try again'}</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const DEMO_ATHLETES = [
  {
    key: 'alex', name: 'Alex Johnsson', flag: '🇸🇪', sport: 'tennis', age: 32, tier: 'headcoach',
    gender: 'male', level: 'Club player', goals: ['Improve match consistency', 'Win more close sets'],
    goal: 'Improve match consistency and win more close sets',
    tagline: 'Club tennis player logging every match and building a performance history.',
    data: null,
  },
];
/* ─── Data Coach icon ──────────────────────────────────────────────── */
const triggerMicrosoftSignIn = async () => {
  await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: { redirectTo: window.location.origin, scopes: 'email' },
  });
};
const exportToExcel = (data, filename) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};
const exportToPDF = (data, columns, filename) => {
  const doc = new jsPDF();
  autoTable(doc, {
    head: [columns],
    body: data.map(row => columns.map(col => row[col] || '')),
  });
  doc.save(`${filename}.pdf`);
};
const exportChartsToPDF = async (sectionName) => {
  // Progress overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:9999;display:flex;align-items:center;justify-content:center;';
  const box = document.createElement('div');
  box.style.cssText = 'background:#1e293b;border:1px solid #334155;border-radius:14px;padding:28px 36px;text-align:center;min-width:220px;';
  box.innerHTML = `
    <div style="font-size:28px;margin-bottom:10px">📄</div>
    <div style="color:#e2e8f0;font-size:15px;font-weight:bold;margin-bottom:6px">Luodaan PDF...</div>
    <div id="pdf-progress-text" style="color:#94a3b8;font-size:13px;margin-bottom:14px">Valmistellaan</div>
    <div style="background:#0f172a;border-radius:99px;height:6px;overflow:hidden;">
      <div id="pdf-progress-bar" style="height:100%;width:0%;background:#00d4ff;border-radius:99px;transition:width 0.3s ease;"></div>
    </div>`;
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  const setProgress = (text, pct) => {
    document.getElementById('pdf-progress-text').textContent = text;
    document.getElementById('pdf-progress-bar').style.width = pct + '%';
  };
  try {
  const doc = new jsPDF('p', 'mm', 'a4');
  const cards = document.querySelectorAll('[data-chart-card]');
  let added = 0;
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const title = card.dataset.title || '';
    const rawData = JSON.parse(card.dataset.rawdata || '[]');
    const rawSecondary = JSON.parse(card.dataset.rawsecondarydata || '[]');
    setProgress(`Graafi ${i + 1} / ${cards.length}: ${title}`, Math.round(((i) / cards.length) * 90));
    const canvas = await html2canvas(card, { backgroundColor: '#1e293b', scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const clampedImgHeight = Math.min(imgHeight, 130);
    if (added > 0) doc.addPage();
    // Header
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`ZenAnalytics — ${sectionName}`, 10, 7);
    doc.setFontSize(12);
    doc.setTextColor(220, 220, 220);
    doc.text(title, 10, 14);
    // Chart image
    doc.addImage(imgData, 'PNG', 10, 18, imgWidth, clampedImgHeight);
    // Data table below chart
    const isPercent = title.includes('%');
    const fmt = (v) => v != null ? String(v) + (isPercent ? '%' : '') : '—';
    const tableBody = rawData.map((row, idx) => [
      row.label || row.date || '',
      row.opponent || '—',
      fmt(row.y),
      fmt(rawSecondary[idx]?.y),
    ]);
    if (tableBody.length > 0) {
      autoTable(doc, {
        startY: 20 + clampedImgHeight + 4,
        head: [['Date', 'Opponent', 'You', 'Opp. Value']],
        body: tableBody,
        styles: { fontSize: 8, cellPadding: 2, textColor: [220, 220, 220], fillColor: [30, 41, 59] },
        headStyles: { fillColor: [0, 100, 120], textColor: [0, 212, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [15, 23, 42] },
        margin: { left: 10, right: 10 },
      });
    }
    added++;
  }
  if (added > 0) {
    setProgress('Tallennetaan PDF...', 95);
    await new Promise(r => setTimeout(r, 200));
    doc.save(`ZenAnalytics_${sectionName}_${todayLocal()}.pdf`);
  }
  setProgress('Valmis! ✅', 100);
  await new Promise(r => setTimeout(r, 700));
  } finally {
    document.body.removeChild(overlay);
  }
};
const chartExportBtnStyle = {
  background: 'transparent',
  border: '1px solid #4ade80',
  color: '#4ade80',
  borderRadius: '6px',
  padding: '6px 14px',
  cursor: 'pointer',
  fontSize: '13px',
  marginBottom: '12px',
};
const exportBtnStyle = {
  padding: '4px 10px',
  fontSize: '11px',
  background: 'transparent',
  color: '#e2e8f0',
  border: '1px solid #4ade80',
  borderRadius: '5px',
  cursor: 'pointer',
  marginRight: '6px',
};
const T = {
  appSubtitle: 'Performance analytics system for racket sport players',
  matchStats: 'Match Stats',
  shotAnalytics: 'Shot Analytics',
  match: 'Match',
  set: 'Set',
  optional: 'optional',
  analyzing: 'Analyzing...',
  opponent: 'Opponent',
  aces: 'Aces',
  unreturnedServes: 'Unreturned serves',
  fastestServe: 'Fastest serve',
  doubleFaults: 'Double faults',
  firstServeIn: '1st Serve In',
  firstServeWon: '1st Serve Won',
  avgFirstServe: 'Avg. 1st Serve Speed',
  secondServeIn: '2nd Serve In',
  secondServeWon: '2nd Serve Won',
  avgSecondServe: 'Avg. 2nd Serve Speed',
  firstReturn: '1st Serve Return',
  secondReturn: '2nd Serve Return',
  totalPoints: 'Total Points Won',
  winners: 'Winners',
  forehandWinners: 'Forehand winners',
  backhandWinners: 'Backhand winners',
  errors: 'Errors',
  forehandErrors: 'Forehand errors',
  backhandErrors: 'Backhand errors',
  shortRallies: 'Short Rallies (1-4)',
  mediumRallies: 'Medium Rallies (5-8)',
  longRallies: 'Long Rallies (9+)',
  avgSpeed: 'Avg. speed',
  depth: 'Depth',
  fastestShot: 'Fastest shot',
  shotsIn: 'Shots in',
  forehandsIn: 'Forehands in',
  backhandsIn: 'Backhands in',
  totalErrors: 'Total errors',
  out: 'Out',
  net: 'Net',
  groundstrokeSpeed: 'Groundstroke speed',
  groundstrokeDepth: 'Groundstroke depth',
  groundstrokeNetClr: 'Groundstroke net clearance',
  forehandSpeed: 'Forehand speed',
  forehandDepth: 'Forehand depth',
  forehandNetClr: 'Forehand net clearance',
  backhandSpeed: 'Backhand speed',
  backhandDepth: 'Backhand depth',
  backhandNetClr: 'Backhand net clearance',
};
const SECTIONS = [
  {
    id: 'match_stats',
    label: T.matchStats,
    icon: '📊',
    levels: [
      { id: 'match', label: T.match, required: true },
      { id: 'set1', label: `${T.set} 1`, required: false },
      { id: 'set2', label: `${T.set} 2`, required: false },
      { id: 'set3', label: `${T.set} 3`, required: false },
    ]
  },
  {
    id: 'shot_analytics',
    label: T.shotAnalytics,
    icon: '🎯',
    levels: [
      { id: 'match', label: T.match, required: true },
      { id: 'set1', label: `${T.set} 1`, required: false },
      { id: 'set2', label: `${T.set} 2`, required: false },
      { id: 'set3', label: `${T.set} 3`, required: false },
    ]
  }
];
const SET_NUMBER = { match: 0, set1: 1, set2: 2, set3: 3 };
// ─────────────────────────────────────────────
// UPLOAD RESULT SUMMARY
// ─────────────────────────────────────────────
const SECTION_DISPLAY = {
  match_stats:    { label: 'Match Stats',    icon: '📊' },
  shot_analytics: { label: 'Shot Analytics', icon: '🎯' },
};
function formatDate(dateStr) {
  if (!dateStr) return '';
  return formatDateLocaleAware(dateStr);
}
// ─────────────────────────────────────────────
// ANALYTICS VIEW (placeholder)
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// ANALYTICS HELPERS
// ─────────────────────────────────────────────
const OPPONENTS = ['All opponents'];
const SET_FILTER_OPTIONS = [
  { id: 0, label: 'Match' },
  { id: 1, label: 'Set 1' },
  { id: 2, label: 'Set 2' },
  { id: 3, label: 'Set 3' },
];
function pct(made, total) {
  if (made == null || total == null || total === 0) return null;
  return Math.round((made / total) * 100);
}
// Simple sparkline SVG chart
const RESULT_COLOR = { W: '#16a34a', L: '#dc2626', D: '#94a3b8' };
const resultDotColor = (r, fallback) => RESULT_COLOR[r] || fallback;
const resultLabel = { W: '✓ Won', L: '✗ Lost', D: '= Tied' };
function LineChart({ data, color = '#38bdf8', secondaryData = null, secondaryColor = '#f97316', height = 80, hideSecondaryAxis = false }) {
  const isMobile = window.innerWidth < 640;
  const [tooltip, setTooltip] = useState(null);
  if (!data || data.length < 2) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#cbd5e1', fontSize: '12px' }}>Not enough data</span>
      </div>
    );
  }
  const validData = data.filter(d => d.y != null);
  const validSecondary = secondaryData ? secondaryData.filter(d => d.y != null) : [];
  if (validData.length < 2) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#cbd5e1', fontSize: '12px' }}>Not enough data</span>
      </div>
    );
  }
  const W = 300, H = height;
  const pad = { t: 8, r: 8, b: 24, l: 36 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const allY = [...validData.map(d => d.y)];
  const rawMinY1 = Math.min(...allY);
  const rawMaxY1 = Math.max(...allY);
  const rawRangeY1 = rawMaxY1 - rawMinY1 || 1;
  const minY1 = rawMinY1 - rawRangeY1 * 0.1;
  const maxY1 = rawMaxY1 + rawRangeY1 * 0.1;
  const rangeY1 = maxY1 - minY1;
  const dotR = validData.length <= 8 ? 6 : validData.length <= 15 ? 4 : 3;
  let minY2, maxY2, rangeY2;
  if (validSecondary.length >= 2) {
    const rawMinY2 = Math.min(...validSecondary.map(d => d.y));
    const rawMaxY2 = Math.max(...validSecondary.map(d => d.y));
    const rawRangeY2 = rawMaxY2 - rawMinY2 || 1;
    minY2 = rawMinY2 - rawRangeY2 * 0.1;
    maxY2 = rawMaxY2 + rawRangeY2 * 0.1;
    rangeY2 = maxY2 - minY2;
  }
  const xScale = (i, total) => pad.l + (i / (total - 1)) * innerW;
  const yScale1 = (v) => pad.t + innerH - ((v - minY1) / rangeY1) * innerH;
  const yScale2 = (v) => pad.t + innerH - ((v - minY2) / rangeY2) * innerH;
  const makePath = (pts, ys) =>
    pts.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i, pts.length).toFixed(1)} ${ys(d.y).toFixed(1)}`).join(' ');
  const xLabels = validData.map((d, i) => ({
    x: xScale(i, validData.length),
    label: d.label || '',
  }));
  const bandW = validData.length > 1 ? innerW / (validData.length - 1) : innerW;
  const ttD = tooltip !== null ? validData[tooltip] : null;
  const ttS = (tooltip !== null && secondaryData)
    ? secondaryData.find(d => d.date && ttD?.date ? d.date === ttD.date : false) ?? (validSecondary[tooltip] || null)
    : null;
  const ttXPct = tooltip !== null ? (xScale(tooltip, validData.length) / W * 100).toFixed(1) : 0;
  return (
    <div style={{ position: 'relative' }}>
      {!isMobile && ttD && (
        <div style={{
          position: 'absolute',
          left: `${ttXPct}%`,
          top: '-4px',
          transform: `translateX(${parseFloat(ttXPct) < 20 ? '0%' : parseFloat(ttXPct) > 80 ? '-100%' : '-50%'})`,
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '8px',
          padding: '6px 10px',
          zIndex: 10,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          fontSize: '11px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
        }}>
          {ttD.date && <p style={{ margin: 0, color: '#cbd5e1', fontSize: '10px' }}>{formatDate(ttD.date)}</p>}
          {ttD.opponent && <p style={{ margin: 0, color: '#cbd5e1', fontSize: '10px' }}>vs {ttD.opponent}</p>}
          {ttD.result && (
            <p style={{ margin: '2px 0 0', color: RESULT_COLOR[ttD.result] || '#94a3b8', fontWeight: 'bold', fontSize: '11px' }}>
              {resultLabel[ttD.result]}
            </p>
          )}
          <p style={{ margin: '2px 0 0', color: color, fontWeight: 'bold', fontSize: '12px' }}>
            You: {ttD.y != null ? ttD.y : '—'}
          </p>
          {ttS && ttS.y != null && (
            <p style={{ margin: '1px 0 0', color: secondaryColor, fontSize: '12px' }}>
              Opp: {ttS.y}
            </p>
          )}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height }} preserveAspectRatio="none"
        onMouseLeave={() => !isMobile && setTooltip(null)}>
        {/* Grid lines — horizontal + vertical */}
        {[0, 1/3, 2/3, 1].map((t, i) => (
          <line key={i}
            x1={pad.l} y1={pad.t + innerH * (1 - t)}
            x2={W - pad.r} y2={pad.t + innerH * (1 - t)}
            stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.25" />
        ))}
        {validData.map((_, i) => (
          <line key={i}
            x1={xScale(i, validData.length)} y1={pad.t}
            x2={xScale(i, validData.length)} y2={pad.t + innerH}
            stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.25" />
        ))}
        {/* Y axis labels */}
        {[0, 1/3, 2/3, 1].map((t, i) => (
          <text key={i} x={pad.l - 4} y={pad.t + innerH * (1 - t) + 4}
            textAnchor="end" fill="#cbd5e1" fontSize="9">
            {Math.round(minY1 + rangeY1 * t)}
          </text>
        ))}
        {/* Secondary Y axis labels */}
        {validSecondary.length >= 2 && !hideSecondaryAxis && [0, 1/3, 2/3, 1].map((t, i) => (
          <text key={i} x={W - pad.r + 4} y={pad.t + innerH * (1 - t) + 4}
            textAnchor="start" fill={secondaryColor} fontSize="9" opacity="0.7">
            {Math.round(minY2 + rangeY2 * t)}
          </text>
        ))}
        {/* Primary line */}
        <path d={makePath(validData, yScale1)} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
        {/* Primary dots — colored by match result if available */}
        {validData.map((d, i) => (
          <circle key={i} cx={xScale(i, validData.length)} cy={yScale1(d.y)} r={dotR}
            fill={resultDotColor(d.result, color)}
            stroke={tooltip === i ? '#fff' : 'none'} strokeWidth="1.5" />
        ))}
        {/* Secondary line */}
        {validSecondary.length >= 2 && (
          <>
            <path d={makePath(validSecondary, yScale2)} fill="none" stroke={secondaryColor} strokeWidth="1.4" strokeLinejoin="round" />
            {validSecondary.map((d, i) => (
              <circle key={i} cx={xScale(i, validSecondary.length)} cy={yScale2(d.y)} r={dotR} fill={secondaryColor} />
            ))}
          </>
        )}
        {/* X axis date labels — max 6, evenly spaced */}
        {(() => {
          const n = xLabels.length;
          const count = Math.min(6, n);
          const indices = n <= 3
            ? xLabels.map((_, i) => i)
            : Array.from({ length: count }, (_, i) => Math.round(i * (n - 1) / (count - 1)));
          return [...new Set(indices)].map(idx => (
            <text key={idx} x={xLabels[idx].x} y={H - 4}
              textAnchor="middle" fill="#cbd5e1" fontSize="8">
              {xLabels[idx].label}
            </text>
          ));
        })()}
        {/* Invisible hit areas per data point — desktop only */}
        {!isMobile && validData.map((d, i) => {
          const cx = xScale(i, validData.length);
          return (
            <rect key={i}
              x={cx - bandW / 2} y={pad.t}
              width={bandW} height={innerH}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setTooltip(i)}
            />
          );
        })}
        {/* Highlighted vertical line at hover — desktop only */}
        {!isMobile && tooltip !== null && (
          <line
            x1={xScale(tooltip, validData.length)} y1={pad.t}
            x2={xScale(tooltip, validData.length)} y2={pad.t + innerH}
            stroke="#475569" strokeWidth="1" strokeDasharray="3 2" />
        )}
      </svg>
    </div>
  );
}
function ChartCard({ title, legend, children, controls }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '6px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <p style={{ margin: 0, color: '#e2e8f0', fontSize: '13px', fontWeight: 'bold' }}>{title}</p>
          {controls && <div style={{ flexShrink: 0 }}>{controls}</div>}
        </div>
        {legend && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {legend.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: 16, height: 2, background: l.color, borderRadius: 1, borderTop: l.dashed ? '2px dashed ' + l.color : undefined }} />
                <span style={{ color: '#cbd5e1', fontSize: '10px' }}>{l.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
function AnalyticsChart({ title, legend, data, secondaryData, color = '#38bdf8', secondaryColor = '#f97316', height = 100, dataFilter = 'all', hideSecondaryAxis = false }) {
  const [fullscreen, setFullscreen] = useState(false);
  // Compute effective data based on dataFilter
  const effData = dataFilter === 'opponent' ? secondaryData : data;
  const effSecondary = dataFilter === 'all' ? secondaryData : null;
  const effColor = dataFilter === 'opponent' ? secondaryColor : color;
  const chartEl = (h) => (
    <LineChart key={title} data={effData} secondaryData={effSecondary} color={effColor} secondaryColor={secondaryColor} height={h} hideSecondaryAxis={hideSecondaryAxis} />
  );
  const controls = (
    <button onClick={() => setFullscreen(true)}
      style={{ background: 'transparent', border: '1px solid #334155', borderRadius: '5px', padding: '2px 7px', color: '#cbd5e1', fontSize: '11px', cursor: 'pointer', lineHeight: 1 }}>
      ⛶
    </button>
  );
  return (
    <>
      <ChartCard title={title} legend={legend} controls={controls}>
        {chartEl(height)}
      </ChartCard>
      {fullscreen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={() => setFullscreen(false)}>
          <div style={{ background: '#1e293b', borderRadius: '16px', padding: '20px', width: '100%', maxWidth: '600px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <p style={{ margin: 0, color: '#e2e8f0', fontSize: '14px', fontWeight: 'bold' }}>{title}</p>
              <button onClick={() => setFullscreen(false)}
                style={{ background: 'none', border: '1px solid #475569', borderRadius: '6px', color: '#cbd5e1', fontSize: '13px', padding: '3px 10px', cursor: 'pointer' }}>
                ✕
              </button>
            </div>
            {legend && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {legend.map((l, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: 16, height: 2, background: l.color, borderTop: l.dashed ? '2px dashed ' + l.color : undefined }} />
                    <span style={{ color: '#cbd5e1', fontSize: '11px' }}>{l.label}</span>
                  </div>
                ))}
              </div>
            )}
            {chartEl(240)}
          </div>
        </div>
      )}
    </>
  );
}
// ─────────────────────────────────────────────
// ANALYTICS HELPERS
// ─────────────────────────────────────────────
const dateLabel = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr.slice(5) || '';
  return `${parseInt(parts[2])}.${parseInt(parts[1])}`;
};
const shortDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(day).padStart(2, '0')} ${months[month - 1]} ${year}`;
};
const filterByTimeRange = (matchList, timeRange) => {
  if (!timeRange || timeRange === 'All') return matchList;
  const months = { '1M': 1, '3M': 3, '6M': 6, '12M': 12, '24M': 24 };
  const m = months[timeRange];
  if (!m) return matchList;
  // Käytä tämän päivän päivämäärää referenssipisteenä (ei viimeisintä ottelua)
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - m, 1);
  return matchList.filter(match => !match.match_date || new Date(match.match_date) >= cutoff);
};
const aggregateSeries = (rawSeries, timeRange) => {
  if (timeRange === '1M' || rawSeries.length <= 2) return rawSeries;
  const groups = {};
  rawSeries.forEach(d => {
    if (!d.date) return;
    const key = d.date.slice(0, 7); // YYYY-MM
    if (!groups[key]) groups[key] = [];
    groups[key].push(d);
  });
  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => {
      const validY = items.filter(d => d.y != null).map(d => d.y);
      const d = new Date(key + '-01');
      const label = formatDateLocaleAware(key + '-01', { month: 'short' }) + ' ' + String(d.getFullYear()).slice(2);
      return {
        label,
        date: key + '-01',
        opponent: items.length === 1 ? items[0].opponent : `${items.length} matches`,
        result: items.length === 1 ? items[0].result : null,
        y: validY.length > 0 ? Math.round(validY.reduce((a, b) => a + b, 0) / validY.length * 10) / 10 : null,
      };
    });
};
function TimeRangeFilter({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '6px' }}>
      {['1M', '3M', '6M', '12M', '24M'].map(r => (
        <button key={r} onClick={() => onChange(r)} className="filter-btn" style={{
          background: value === r ? '#0ea5e9' : '#1e293b',
          color: value === r ? '#fff' : '#94a3b8',
          border: 'none', borderRadius: '8px', padding: '7px 16px',
          fontSize: '12px', fontWeight: '600', cursor: 'pointer',
        }}>{r}</button>
      ))}
    </div>
  );
}
function ExportPDFButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: hovered ? '#94a3b8' : '#64748b', padding: '4px',
          display: 'flex', alignItems: 'center', borderRadius: '6px',
          transition: 'color 0.15s',
        }}
        title="Export all reports to PDF"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </button>
      {hovered && (
        <div style={{
          position: 'absolute', right: 0, top: '28px',
          background: '#1e293b', border: '1px solid #334155',
          borderRadius: '6px', padding: '5px 10px',
          fontSize: '11px', color: '#cbd5e1',
          whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
        }}>
          Export all reports to PDF
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ANALYTICS VIEW
// ─────────────────────────────────────────────
function AnalyticsView({ playerId, isDemoMode }) {
  const [matches, setMatches] = useState([]);
  const [allStats, setAllStats] = useState({});
  const [serves, setServes] = useState([]);
  const [returns, setReturns] = useState([]);
  const [points, setPoints] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [dbStatus, setDbStatus] = useState('idle'); // 'idle' | 'loading' | 'ready'
  const [showReports, setShowReports] = useState(false);
  const [activeSection, setActiveSection] = useState('serve');
  const [setFilter, setSetFilter] = useState(0);
  const [opponentFilter, setOpponentFilter] = useState('All opponents');
  const [opponents, setOpponents] = useState(['All opponents']);
  const [timeRange, setTimeRange] = useState('12M');
  const [dataFilter, setDataFilter] = useState('all');
  const [zoomChart, setZoomChart] = useState(null);
  const fetchAll = async () => {
    if (!isDemoMode && !playerId) return;
    try {
      // Demo mode uses dedicated endpoints; regular users use auth-gated endpoints
      const [matchRes, statsRes] = await Promise.all([
        isDemoMode
          ? axios.get(`${API}/demo/matches`)
          : axios.get(`${API}/player/${playerId}/matches`),
        isDemoMode
          ? axios.get(`${API}/demo/analytics`)
          : axios.get(`${API}/player/${playerId}/stats`),
      ]);
      const matchList = matchRes.data;
      const statsData = statsRes.data; // { serves: [...all rows...], returns: [...], ... }
      console.log('matches after fetch:', matchList.length);
      console.log('serves after fetch:', statsData.serves.length);
      console.log('first serve row:', statsData.serves[0]);
      // Build allStats map keyed by match_id (for getStat / chart series)
      const statsMap = {};
      matchList.forEach(m => {
        statsMap[m.id] = {
          serves:             statsData.serves.filter(r => r.match_id === m.id),
          returns:            statsData.returns.filter(r => r.match_id === m.id),
          points:             statsData.points.filter(r => r.match_id === m.id),
          shot_all:           statsData.shot_all.filter(r => r.match_id === m.id),
          shot_groundstrokes: statsData.shot_groundstrokes.filter(r => r.match_id === m.id),
          shot_forehand:      statsData.shot_forehand.filter(r => r.match_id === m.id),
          shot_backhand:      statsData.shot_backhand.filter(r => r.match_id === m.id),
        };
      });
      // Update all state at once — React 18 batches these into a single render
      const opps = [...new Set(matchList.map(m => m.opponent_name).filter(Boolean))];
      const filteredMatchList = filterByTimeRange(matchList, timeRange)
        .filter(m => opponentFilter === 'All opponents' || m.opponent_name === opponentFilter)
        .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
      setMatches(matchList);
      setFilteredMatches(filteredMatchList);
      setOpponents(['All opponents', ...opps]);
      setAllStats(statsMap);
      setServes(statsData.serves);
      setReturns(statsData.returns);
      setPoints(statsData.points);
    } catch (e) { console.error(e); }
  };
  const refresh = () => { fetchAll(); };
  // Auto-load in demo mode (skip manual button)
  useEffect(() => {
    if (isDemoMode) {
      setDbStatus('loading');
      fetchAll().then(() => { setDbStatus('ready'); setShowReports(true); });
    }
  }, [isDemoMode]);
  // Recompute filteredMatches when timeRange or opponentFilter changes after initial load
  useEffect(() => {
    const filtered = filterByTimeRange(matches, timeRange)
      .filter(m => opponentFilter === 'All opponents' || m.opponent_name === opponentFilter)
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
    setFilteredMatches(filtered);
  }, [timeRange, opponentFilter, matches]);
  // Helper: get stat value for a match at current set filter
  const getStat = (matchId, table, field) => {
    const matchStats = allStats[matchId];
    if (!matchStats) return null;
    const rows = matchStats[table];
    if (!rows) return null;
    const row = rows.find(r => Number(r.set_number) === Number(setFilter));
    return row ? row[field] : null;
  };
  // Build chart data series — last 12 matches, no aggregation
  const series = (table, field, transform) =>
    filteredMatches.slice(-12).map(m => ({
      label: dateLabel(m.match_date),
      date: m.match_date,
      opponent: m.opponent_name,
      result: m.result || null,
      y: (() => { const v = getStat(m.id, table, field); return transform ? transform(v, m.id, table) : v; })(),
    }));
  const pctSeries = (table, made, total) =>
    filteredMatches.slice(-12).map(m => ({
      label: dateLabel(m.match_date),
      date: m.match_date,
      opponent: m.opponent_name,
      result: m.result || null,
      y: pct(getStat(m.id, table, made), getStat(m.id, table, total)),
    }));
  const rawSeries = (table, field, transform) =>
    filteredMatches.map(m => {
      const v = getStat(m.id, table, field);
      return {
        label: dateLabel(m.match_date),
        date: m.match_date,
        opponent: m.opponent_name,
        result: m.result || null,
        y: transform ? transform(v, m.id, table) : v,
      };
    });
  const rawPctSeries = (table, made, total) =>
    filteredMatches.map(m => ({
      label: dateLabel(m.match_date),
      date: m.match_date,
      opponent: m.opponent_name,
      result: m.result || null,
      y: pct(getStat(m.id, table, made), getStat(m.id, table, total)),
    }));
  const Chart = ({ rawData, rawSecondaryData, ...props }) => (
    <div
      data-chart-card
      data-title={props.title || ''}
      data-rawdata={JSON.stringify(rawData || [])}
      data-rawsecondarydata={JSON.stringify(rawSecondaryData || [])}
      style={{ position: 'relative' }}
    >
      <AnalyticsChart {...props} dataFilter={dataFilter} />
      <button
        onClick={() => {
          const zd = (rawData || props.data).slice(-25);
          const zs = rawSecondaryData ? rawSecondaryData.slice(-25) : props.secondaryData;
          setZoomChart({ ...props, data: zd, secondaryData: zs });
        }}
        style={{
          position: 'absolute', top: '8px', right: '8px',
          background: 'rgba(15,23,42,0.75)', border: '1px solid #334155',
          borderRadius: '4px', padding: '4px 5px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', color: '#ffffff',
        }}
        title="Zoom"
      >
        <ZoomIn size={13} />
      </button>
    </div>
  );
  const SECTIONS = [
    { id: 'serve', label: '🎾 Serve' },
    { id: 'return', label: '↩️ Return' },
    { id: 'points', label: '🏆 Points' },
    { id: 'shot', label: '⚡ Shot' },
  ];
  const DATA_SECTIONS = [
    { id: 'data_serve', label: '🎾 Serve Data' },
    { id: 'data_return', label: '↩️ Return Data' },
    { id: 'data_shot', label: '⚡ Shot Data' },
    { id: 'data_points', label: '🏆 Points Data' },
  ];
  // Shared filter panel helpers
  const HDR = { margin: '0 0 6px 2px', fontSize: '9px', fontWeight: '700', letterSpacing: '0.1em', color: '#ffffff', textTransform: 'uppercase' };
  const btn = (active, activeColor = '#0ea5e9') => ({
    background: active ? activeColor : '#1e293b',
    color: active ? '#fff' : '#94a3b8',
    border: 'none', borderRadius: '8px', padding: '7px 16px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  });
  if (!playerId) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <p style={{ color: '#cbd5e1' }}>Set up your profile in Settings first</p>
      </div>
    );
  }
  if (dbStatus === 'idle' || (dbStatus === 'loading')) {
    const isLoading = dbStatus === 'loading';
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
      }}>
        <button
          disabled={isLoading}
          onClick={async () => {
            setDbStatus('loading');
            await fetchAll();
            setDbStatus('ready');
            setShowReports(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
            border: 'none',
            borderRadius: '20px',
            padding: '22px 56px',
            fontSize: '18px',
            fontWeight: '700',
            color: isLoading ? 'rgba(255,255,255,0.65)' : '#fff',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: isLoading
              ? '0 0 20px rgba(99,102,241,0.25), 0 4px 20px rgba(0,0,0,0.4)'
              : '0 0 36px rgba(99,102,241,0.55), 0 4px 28px rgba(0,0,0,0.45)',
            letterSpacing: '0.02em',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            opacity: isLoading ? 0.85 : 1,
            transition: 'box-shadow 0.2s, opacity 0.2s',
          }}
        >
          {isLoading ? (
            <>
              <span style={{
                display: 'inline-block',
                width: '18px', height: '18px',
                border: '2.5px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.75s linear infinite',
                flexShrink: 0,
              }} />
              Loading data...
            </>
          ) : (
            '📊 Load & View Reports'
          )}
        </button>
      </div>
    );
  }
  return (
    <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ color: '#38bdf8', fontSize: '18px', margin: 0 }}>📊 Analytics</h2>
        {['serve','return','points','shot'].includes(activeSection) && (
          <ExportPDFButton onClick={() => {
            const sectionMap = { serve: 'Serve', return: 'Return', points: 'Points', shot: 'Shot Analytics' };
            exportChartsToPDF(sectionMap[activeSection]);
          }} />
        )}</div>
      {/* OPPONENTS */}
      <div style={{ marginBottom: '12px' }}>
        <p style={HDR}>Opponents</p>
        <div style={{ overflowX: 'auto', display: 'flex', gap: '6px', paddingBottom: '4px' }}>
          {opponents.map(opp => (
            <button key={opp} onClick={() => setOpponentFilter(opp)}
              className="filter-btn" style={btn(opponentFilter === opp)}>
              {opp}
            </button>
          ))}
        </div>
      </div>
      {/* TIMELINE */}
      <div style={{ marginBottom: '12px' }}>
        <p style={HDR}>Timeline</p>
        <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
      </div>
      {/* PLAYERS */}
      <div style={{ marginBottom: '12px' }}>
        <p style={HDR}>Players</p>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[{ id: 'all', label: 'All' }, { id: 'you', label: 'You' }, { id: 'opponent', label: 'Opponent' }].map(opt => (
            <button key={opt.id} onClick={() => setDataFilter(opt.id)}
              className="filter-btn" style={btn(dataFilter === opt.id)}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {/* MATCH / SET */}
      <div style={{ marginBottom: '12px' }}>
        <p style={HDR}>Match / Set</p>
        <div style={{ display: 'flex', gap: '6px' }}>
          {SET_FILTER_OPTIONS.map(opt => (
            <button key={opt.id} onClick={() => setSetFilter(opt.id)}
              className="filter-btn" style={btn(setFilter === opt.id)}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {filteredMatches.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <p style={{ color: '#cbd5e1', fontSize: '14px' }}>
            {matches.length === 0 ? 'No match data yet — upload some matches first!' : 'No data for selected time period'}
          </p>
        </div>
      ) : <>
      {/* GRAPHICAL REPORTS */}
      <div style={{ marginBottom: '12px' }}>
        <p style={HDR}>Graphical Reports</p>
        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className="filter-btn" style={btn(activeSection === s.id, '#0ea5e9')}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {/* TABLE DATA */}
      <div style={{ marginBottom: '16px' }}>
        <p style={HDR}>Table Data</p>
        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px' }}>
          {DATA_SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className="filter-btn" style={btn(activeSection === s.id, '#10b981')}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {/* ── SERVE DATA TABLE ── */}
      {activeSection === 'data_serve' && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: '8px' }}>
            <button style={exportBtnStyle} onClick={() => exportToExcel(
              filteredMatches.map(m => {
                const s = serves.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', '1st In%': pct(s?.own_first_serve_in_made, s?.own_first_serve_in_total) ?? '', '1st Won%': pct(s?.own_first_serve_won_made, s?.own_first_serve_won_total) ?? '', '2nd In%': pct(s?.own_second_serve_in_made, s?.own_second_serve_in_total) ?? '', '2nd Won%': pct(s?.own_second_serve_won_made, s?.own_second_serve_won_total) ?? '', 'Avg 1st km/h': s?.own_avg_first_serve_speed_kmh ?? '', 'Avg 2nd km/h': s?.own_avg_second_serve_speed_kmh ?? '', Fastest: s?.own_fastest_serve_kmh ?? '', Aces: s?.own_aces ?? '', DFs: s?.own_double_faults ?? '' };
              }), 'serve_stats')}>📊 Export Excel</button>
            <button style={exportBtnStyle} onClick={() => {
              const cols = ['Date','Opponent','1st In%','1st Won%','2nd In%','2nd Won%','Avg 1st km/h','Avg 2nd km/h','Fastest','Aces','DFs'];
              exportToPDF(filteredMatches.map(m => {
                const s = serves.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', '1st In%': pct(s?.own_first_serve_in_made, s?.own_first_serve_in_total) ?? '', '1st Won%': pct(s?.own_first_serve_won_made, s?.own_first_serve_won_total) ?? '', '2nd In%': pct(s?.own_second_serve_in_made, s?.own_second_serve_in_total) ?? '', '2nd Won%': pct(s?.own_second_serve_won_made, s?.own_second_serve_won_total) ?? '', 'Avg 1st km/h': s?.own_avg_first_serve_speed_kmh ?? '', 'Avg 2nd km/h': s?.own_avg_second_serve_speed_kmh ?? '', Fastest: s?.own_fastest_serve_kmh ?? '', Aces: s?.own_aces ?? '', DFs: s?.own_double_faults ?? '' };
              }), cols, 'serve_stats');
            }}>📄 Export PDF</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Opponent</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>1st In%</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>1st Won%</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>2nd In%</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>2nd Won%</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Avg 1st km/h</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Avg 2nd km/h</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Fastest</th>
                <th style={{ color: '#22c55e', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Aces</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>DFs</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map(m => {
                const s = serves.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ color: '#e2e8f0', padding: '5px 8px', whiteSpace: 'nowrap' }}>{m.match_date ? shortDate(m.match_date) : '—'}</td>
                    <td style={{ color: '#cbd5e1', padding: '5px 8px', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.opponent_name || '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(s?.own_first_serve_in_made, s?.own_first_serve_in_total) ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(s?.own_first_serve_won_made, s?.own_first_serve_won_total) ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(s?.own_second_serve_in_made, s?.own_second_serve_in_total) ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(s?.own_second_serve_won_made, s?.own_second_serve_won_total) ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{s?.own_avg_first_serve_speed_kmh ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{s?.own_avg_second_serve_speed_kmh ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{s?.own_fastest_serve_kmh ?? '—'}</td>
                    <td style={{ color: '#22c55e', padding: '5px 8px', textAlign: 'right' }}>{s?.own_aces ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{s?.own_double_faults ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ color: '#cbd5e1', fontSize: '10px', marginTop: '8px', textAlign: 'right' }}>
            {filteredMatches.length} matches · {setFilter === 0 ? 'Match' : `Set ${setFilter}`}
          </p>
        </div>
      )}
      {/* ── RETURN DATA TABLE ── */}
      {activeSection === 'data_return' && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: '8px' }}>
            <button style={exportBtnStyle} onClick={() => exportToExcel(
              filteredMatches.map(m => {
                const r = returns.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', '1st Ret% (You)': pct(r?.own_first_serve_return_made, r?.own_first_serve_return_total) ?? '', '2nd Ret% (You)': pct(r?.own_second_serve_return_made, r?.own_second_serve_return_total) ?? '', '1st Ret% (Opp)': pct(r?.opp_first_serve_return_made, r?.opp_first_serve_return_total) ?? '', '2nd Ret% (Opp)': pct(r?.opp_second_serve_return_made, r?.opp_second_serve_return_total) ?? '' };
              }), 'return_stats')}>📊 Export Excel</button>
            <button style={exportBtnStyle} onClick={() => {
              const cols = ['Date','Opponent','1st Ret% (You)','2nd Ret% (You)','1st Ret% (Opp)','2nd Ret% (Opp)'];
              exportToPDF(filteredMatches.map(m => {
                const r = returns.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', '1st Ret% (You)': pct(r?.own_first_serve_return_made, r?.own_first_serve_return_total) ?? '', '2nd Ret% (You)': pct(r?.own_second_serve_return_made, r?.own_second_serve_return_total) ?? '', '1st Ret% (Opp)': pct(r?.opp_first_serve_return_made, r?.opp_first_serve_return_total) ?? '', '2nd Ret% (Opp)': pct(r?.opp_second_serve_return_made, r?.opp_second_serve_return_total) ?? '' };
              }), cols, 'return_stats');
            }}>📄 Export PDF</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Opponent</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>1st Ret% (You)</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>2nd Ret% (You)</th>
                <th style={{ color: '#f97316', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>1st Ret% (Opp)</th>
                <th style={{ color: '#f97316', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>2nd Ret% (Opp)</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map(m => {
                const r = returns.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ color: '#e2e8f0', padding: '5px 8px', whiteSpace: 'nowrap' }}>{m.match_date ? shortDate(m.match_date) : '—'}</td>
                    <td style={{ color: '#cbd5e1', padding: '5px 8px', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.opponent_name || '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(r?.own_first_serve_return_made, r?.own_first_serve_return_total) ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(r?.own_second_serve_return_made, r?.own_second_serve_return_total) ?? '—'}</td>
                    <td style={{ color: '#f97316', padding: '5px 8px', textAlign: 'right' }}>{pct(r?.opp_first_serve_return_made, r?.opp_first_serve_return_total) ?? '—'}</td>
                    <td style={{ color: '#f97316', padding: '5px 8px', textAlign: 'right' }}>{pct(r?.opp_second_serve_return_made, r?.opp_second_serve_return_total) ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ color: '#cbd5e1', fontSize: '10px', marginTop: '8px', textAlign: 'right' }}>
            {filteredMatches.length} matches · {setFilter === 0 ? 'Match' : `Set ${setFilter}`}
          </p>
        </div>
      )}
      {/* ── SHOT DATA TABLE ── */}
      {activeSection === 'data_shot' && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: '8px' }}>
            <button style={exportBtnStyle} onClick={() => exportToExcel(
              filteredMatches.map(m => {
                const sa = allStats[m.id]?.shot_all?.find(r => Number(r.set_number) === Number(setFilter));
                const sg = allStats[m.id]?.shot_groundstrokes?.find(r => Number(r.set_number) === Number(setFilter));
                const sfh = allStats[m.id]?.shot_forehand?.find(r => Number(r.set_number) === Number(setFilter));
                const sbh = allStats[m.id]?.shot_backhand?.find(r => Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', 'All Rel%': pct(sa?.own_shots_in_made, sa?.own_shots_in_total) ?? '', 'FH Rel%': pct(sa?.own_forehands_in_made, sa?.own_forehands_in_total) ?? '', 'BH Rel%': pct(sa?.own_backhands_in_made, sa?.own_backhands_in_total) ?? '', 'GS Spd': sg?.own_groundstroke_speed_kmh ?? '', 'GS Depth': sg?.own_groundstroke_depth_m ?? '', 'FH Spd': sfh?.own_forehand_speed_kmh ?? '', 'BH Spd': sbh?.own_backhand_speed_kmh ?? '', Errs: sa?.own_total_errors ?? '', Out: sa?.own_out_count ?? '', Net: sa?.own_net_count ?? '' };
              }), 'shot_stats')}>📊 Export Excel</button>
            <button style={exportBtnStyle} onClick={() => {
              const cols = ['Date','Opponent','All Rel%','FH Rel%','BH Rel%','GS Spd','GS Depth','FH Spd','BH Spd','Errs','Out','Net'];
              exportToPDF(filteredMatches.map(m => {
                const sa = allStats[m.id]?.shot_all?.find(r => Number(r.set_number) === Number(setFilter));
                const sg = allStats[m.id]?.shot_groundstrokes?.find(r => Number(r.set_number) === Number(setFilter));
                const sfh = allStats[m.id]?.shot_forehand?.find(r => Number(r.set_number) === Number(setFilter));
                const sbh = allStats[m.id]?.shot_backhand?.find(r => Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', 'All Rel%': pct(sa?.own_shots_in_made, sa?.own_shots_in_total) ?? '', 'FH Rel%': pct(sa?.own_forehands_in_made, sa?.own_forehands_in_total) ?? '', 'BH Rel%': pct(sa?.own_backhands_in_made, sa?.own_backhands_in_total) ?? '', 'GS Spd': sg?.own_groundstroke_speed_kmh ?? '', 'GS Depth': sg?.own_groundstroke_depth_m ?? '', 'FH Spd': sfh?.own_forehand_speed_kmh ?? '', 'BH Spd': sbh?.own_backhand_speed_kmh ?? '', Errs: sa?.own_total_errors ?? '', Out: sa?.own_out_count ?? '', Net: sa?.own_net_count ?? '' };
              }), cols, 'shot_stats');
            }}>📄 Export PDF</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Opponent</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>All Rel%</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>FH Rel%</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>BH Rel%</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>GS Spd</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>GS Depth</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>FH Spd</th>
                <th style={{ color: '#a78bfa', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>BH Spd</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Errs</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Out</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Net</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map(m => {
                const sa  = allStats[m.id]?.shot_all?.find(r => Number(r.set_number) === Number(setFilter));
                const sg  = allStats[m.id]?.shot_groundstrokes?.find(r => Number(r.set_number) === Number(setFilter));
                const sfh = allStats[m.id]?.shot_forehand?.find(r => Number(r.set_number) === Number(setFilter));
                const sbh = allStats[m.id]?.shot_backhand?.find(r => Number(r.set_number) === Number(setFilter));
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ color: '#e2e8f0', padding: '5px 8px', whiteSpace: 'nowrap' }}>{m.match_date ? shortDate(m.match_date) : '—'}</td>
                    <td style={{ color: '#cbd5e1', padding: '5px 8px', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.opponent_name || '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(sa?.own_shots_in_made, sa?.own_shots_in_total) ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(sa?.own_forehands_in_made, sa?.own_forehands_in_total) ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{pct(sa?.own_backhands_in_made, sa?.own_backhands_in_total) ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{sg?.own_groundstroke_speed_kmh ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{sg?.own_groundstroke_depth_m ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{sfh?.own_forehand_speed_kmh ?? '—'}</td>
                    <td style={{ color: '#a78bfa', padding: '5px 8px', textAlign: 'right' }}>{sbh?.own_backhand_speed_kmh ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{sa?.own_total_errors ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{sa?.own_out_count ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{sa?.own_net_count ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ color: '#cbd5e1', fontSize: '10px', marginTop: '8px', textAlign: 'right' }}>
            {filteredMatches.length} matches · {setFilter === 0 ? 'Match' : `Set ${setFilter}`}
          </p>
        </div>
      )}
      {/* ── POINTS DATA TABLE ── */}
      {activeSection === 'data_points' && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: '8px' }}>
            <button style={exportBtnStyle} onClick={() => exportToExcel(
              filteredMatches.map(m => {
                const p = points.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', Winners: p?.own_winners ?? '', Errors: p?.own_errors ?? '', 'FH Err': p?.own_forehand_errors ?? '', 'BH Err': p?.own_backhand_errors ?? '', 'Short Won': p?.own_short_rallies_won ?? '', 'Med Won': p?.own_medium_rallies_won ?? '', 'Long Won': p?.own_long_rallies_won ?? '' };
              }), 'points_stats')}>📊 Export Excel</button>
            <button style={exportBtnStyle} onClick={() => {
              const cols = ['Date','Opponent','Winners','Errors','FH Err','BH Err','Short Won','Med Won','Long Won'];
              exportToPDF(filteredMatches.map(m => {
                const p = points.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return { Date: m.match_date ? shortDate(m.match_date) : '', Opponent: m.opponent_name || '', Winners: p?.own_winners ?? '', Errors: p?.own_errors ?? '', 'FH Err': p?.own_forehand_errors ?? '', 'BH Err': p?.own_backhand_errors ?? '', 'Short Won': p?.own_short_rallies_won ?? '', 'Med Won': p?.own_medium_rallies_won ?? '', 'Long Won': p?.own_long_rallies_won ?? '' };
              }), cols, 'points_stats');
            }}>📄 Export PDF</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                <th style={{ color: '#cbd5e1', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>Opponent</th>
                <th style={{ color: '#22c55e', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Winners</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Errors</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>FH Err</th>
                <th style={{ color: '#ef4444', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>BH Err</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Short Won</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Med Won</th>
                <th style={{ color: '#38bdf8', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>Long Won</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map(m => {
                const p = points.find(r => r.match_id === m.id && Number(r.set_number) === Number(setFilter));
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ color: '#e2e8f0', padding: '5px 8px', whiteSpace: 'nowrap' }}>{m.match_date ? shortDate(m.match_date) : '—'}</td>
                    <td style={{ color: '#cbd5e1', padding: '5px 8px', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.opponent_name || '—'}</td>
                    <td style={{ color: '#22c55e', padding: '5px 8px', textAlign: 'right' }}>{p?.own_winners ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{p?.own_errors ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{p?.own_forehand_errors ?? '—'}</td>
                    <td style={{ color: '#ef4444', padding: '5px 8px', textAlign: 'right' }}>{p?.own_backhand_errors ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{p?.own_short_rallies_won ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{p?.own_medium_rallies_won ?? '—'}</td>
                    <td style={{ color: '#38bdf8', padding: '5px 8px', textAlign: 'right' }}>{p?.own_long_rallies_won ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ color: '#cbd5e1', fontSize: '10px', marginTop: '8px', textAlign: 'right' }}>
            {filteredMatches.length} matches · {setFilter === 0 ? 'Match' : `Set ${setFilter}`}
          </p>
        </div>
      )}
      {/* ── SERVE ── */}
      {activeSection === 'serve' && <>
        <Chart title="1st Serve In %" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('serves', 'own_first_serve_in_made', 'own_first_serve_in_total')}
          rawData={rawPctSeries('serves', 'own_first_serve_in_made', 'own_first_serve_in_total')}
          secondaryData={pctSeries('serves', 'opp_first_serve_in_made', 'opp_first_serve_in_total')}
          rawSecondaryData={rawPctSeries('serves', 'opp_first_serve_in_made', 'opp_first_serve_in_total')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="1st Serve Won %" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('serves', 'own_first_serve_won_made', 'own_first_serve_won_total')}
          rawData={rawPctSeries('serves', 'own_first_serve_won_made', 'own_first_serve_won_total')}
          secondaryData={pctSeries('serves', 'opp_first_serve_won_made', 'opp_first_serve_won_total')}
          rawSecondaryData={rawPctSeries('serves', 'opp_first_serve_won_made', 'opp_first_serve_won_total')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="2nd Serve In %" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('serves', 'own_second_serve_in_made', 'own_second_serve_in_total')}
          rawData={rawPctSeries('serves', 'own_second_serve_in_made', 'own_second_serve_in_total')}
          secondaryData={pctSeries('serves', 'opp_second_serve_in_made', 'opp_second_serve_in_total')}
          rawSecondaryData={rawPctSeries('serves', 'opp_second_serve_in_made', 'opp_second_serve_in_total')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="2nd Serve Won %" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('serves', 'own_second_serve_won_made', 'own_second_serve_won_total')}
          rawData={rawPctSeries('serves', 'own_second_serve_won_made', 'own_second_serve_won_total')}
          secondaryData={pctSeries('serves', 'opp_second_serve_won_made', 'opp_second_serve_won_total')}
          rawSecondaryData={rawPctSeries('serves', 'opp_second_serve_won_made', 'opp_second_serve_won_total')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Avg. 1st Serve Speed (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('serves', 'own_avg_first_serve_speed_kmh')}
          rawData={rawSeries('serves', 'own_avg_first_serve_speed_kmh')}
          secondaryData={series('serves', 'opp_avg_first_serve_speed_kmh')}
          rawSecondaryData={rawSeries('serves', 'opp_avg_first_serve_speed_kmh')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Fastest Serve (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('serves', 'own_fastest_serve_kmh')}
          rawData={rawSeries('serves', 'own_fastest_serve_kmh')}
          secondaryData={series('serves', 'opp_fastest_serve_kmh')}
          rawSecondaryData={rawSeries('serves', 'opp_fastest_serve_kmh')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Aces" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('serves', 'own_aces')} rawData={rawSeries('serves', 'own_aces')}
          secondaryData={series('serves', 'opp_aces')} rawSecondaryData={rawSeries('serves', 'opp_aces')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Double Faults" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('serves', 'own_double_faults')} rawData={rawSeries('serves', 'own_double_faults')}
          secondaryData={series('serves', 'opp_double_faults')} rawSecondaryData={rawSeries('serves', 'opp_double_faults')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
      </>}
      {/* ── RETURN ── */}
      {activeSection === 'return' && <>
        <Chart title="1st Serve Return %" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('returns', 'own_first_serve_return_made', 'own_first_serve_return_total')}
          rawData={rawPctSeries('returns', 'own_first_serve_return_made', 'own_first_serve_return_total')}
          secondaryData={pctSeries('returns', 'opp_first_serve_return_made', 'opp_first_serve_return_total')}
          rawSecondaryData={rawPctSeries('returns', 'opp_first_serve_return_made', 'opp_first_serve_return_total')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="2nd Serve Return %" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('returns', 'own_second_serve_return_made', 'own_second_serve_return_total')}
          rawData={rawPctSeries('returns', 'own_second_serve_return_made', 'own_second_serve_return_total')}
          secondaryData={pctSeries('returns', 'opp_second_serve_return_made', 'opp_second_serve_return_total')}
          rawSecondaryData={rawPctSeries('returns', 'opp_second_serve_return_made', 'opp_second_serve_return_total')}
          secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
      </>}
      {/* ── POINTS ── */}
      {activeSection === 'points' && <>
        <Chart title="Winners" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_winners')} rawData={rawSeries('points', 'own_winners')}
          secondaryData={series('points', 'opp_winners')} rawSecondaryData={rawSeries('points', 'opp_winners')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Errors" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_errors')} rawData={rawSeries('points', 'own_errors')}
          secondaryData={series('points', 'opp_errors')} rawSecondaryData={rawSeries('points', 'opp_errors')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Forehand Errors" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_forehand_errors')} rawData={rawSeries('points', 'own_forehand_errors')}
          secondaryData={series('points', 'opp_forehand_errors')} rawSecondaryData={rawSeries('points', 'opp_forehand_errors')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Backhand Errors" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_backhand_errors')} rawData={rawSeries('points', 'own_backhand_errors')}
          secondaryData={series('points', 'opp_backhand_errors')} rawSecondaryData={rawSeries('points', 'opp_backhand_errors')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Short Rallies Won (1-4)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_short_rallies_won')} rawData={rawSeries('points', 'own_short_rallies_won')}
          secondaryData={series('points', 'opp_short_rallies_won')} rawSecondaryData={rawSeries('points', 'opp_short_rallies_won')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Medium Rallies Won (5-8)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_medium_rallies_won')} rawData={rawSeries('points', 'own_medium_rallies_won')}
          secondaryData={series('points', 'opp_medium_rallies_won')} rawSecondaryData={rawSeries('points', 'opp_medium_rallies_won')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Long Rallies Won (9+)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('points', 'own_long_rallies_won')} rawData={rawSeries('points', 'own_long_rallies_won')}
          secondaryData={series('points', 'opp_long_rallies_won')} rawSecondaryData={rawSeries('points', 'opp_long_rallies_won')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
      </>}
      {/* ── SHOT ANALYTICS ── */}
      {activeSection === 'shot' && <>
        <Chart title="Groundstroke Speed (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_groundstrokes', 'own_groundstroke_speed_kmh')} rawData={rawSeries('shot_groundstrokes', 'own_groundstroke_speed_kmh')}
          secondaryData={series('shot_groundstrokes', 'opp_groundstroke_speed_kmh')} rawSecondaryData={rawSeries('shot_groundstrokes', 'opp_groundstroke_speed_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Groundstroke Depth (m)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_groundstrokes', 'own_groundstroke_depth_m')} rawData={rawSeries('shot_groundstrokes', 'own_groundstroke_depth_m')}
          secondaryData={series('shot_groundstrokes', 'opp_groundstroke_depth_m')} rawSecondaryData={rawSeries('shot_groundstrokes', 'opp_groundstroke_depth_m')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Groundstroke Fastest (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_groundstrokes', 'own_fastest_groundstroke_kmh')} rawData={rawSeries('shot_groundstrokes', 'own_fastest_groundstroke_kmh')}
          secondaryData={series('shot_groundstrokes', 'opp_fastest_groundstroke_kmh')} rawSecondaryData={rawSeries('shot_groundstrokes', 'opp_fastest_groundstroke_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Forehand Speed (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_forehand', 'own_forehand_speed_kmh')} rawData={rawSeries('shot_forehand', 'own_forehand_speed_kmh')}
          secondaryData={series('shot_forehand', 'opp_forehand_speed_kmh')} rawSecondaryData={rawSeries('shot_forehand', 'opp_forehand_speed_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Forehand Depth (m)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_forehand', 'own_forehand_depth_m')} rawData={rawSeries('shot_forehand', 'own_forehand_depth_m')}
          secondaryData={series('shot_forehand', 'opp_forehand_depth_m')} rawSecondaryData={rawSeries('shot_forehand', 'opp_forehand_depth_m')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Forehand Fastest (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_forehand', 'own_fastest_forehand_kmh')} rawData={rawSeries('shot_forehand', 'own_fastest_forehand_kmh')}
          secondaryData={series('shot_forehand', 'opp_fastest_forehand_kmh')} rawSecondaryData={rawSeries('shot_forehand', 'opp_fastest_forehand_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Backhand Speed (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_backhand', 'own_backhand_speed_kmh')} rawData={rawSeries('shot_backhand', 'own_backhand_speed_kmh')}
          secondaryData={series('shot_backhand', 'opp_backhand_speed_kmh')} rawSecondaryData={rawSeries('shot_backhand', 'opp_backhand_speed_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Backhand Depth (m)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_backhand', 'own_backhand_depth_m')} rawData={rawSeries('shot_backhand', 'own_backhand_depth_m')}
          secondaryData={series('shot_backhand', 'opp_backhand_depth_m')} rawSecondaryData={rawSeries('shot_backhand', 'opp_backhand_depth_m')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Backhand Fastest (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_backhand', 'own_fastest_backhand_kmh')} rawData={rawSeries('shot_backhand', 'own_fastest_backhand_kmh')}
          secondaryData={series('shot_backhand', 'opp_fastest_backhand_kmh')} rawSecondaryData={rawSeries('shot_backhand', 'opp_fastest_backhand_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Reliability All (Shots In %)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('shot_all', 'own_shots_in_made', 'own_shots_in_total')} rawData={rawPctSeries('shot_all', 'own_shots_in_made', 'own_shots_in_total')}
          secondaryData={pctSeries('shot_all', 'opp_shots_in_made', 'opp_shots_in_total')} rawSecondaryData={rawPctSeries('shot_all', 'opp_shots_in_made', 'opp_shots_in_total')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Reliability Forehand (Forehands In %)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('shot_all', 'own_forehands_in_made', 'own_forehands_in_total')} rawData={rawPctSeries('shot_all', 'own_forehands_in_made', 'own_forehands_in_total')}
          secondaryData={pctSeries('shot_all', 'opp_forehands_in_made', 'opp_forehands_in_total')} rawSecondaryData={rawPctSeries('shot_all', 'opp_forehands_in_made', 'opp_forehands_in_total')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Reliability Backhand (Backhands In %)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={pctSeries('shot_all', 'own_backhands_in_made', 'own_backhands_in_total')} rawData={rawPctSeries('shot_all', 'own_backhands_in_made', 'own_backhands_in_total')}
          secondaryData={pctSeries('shot_all', 'opp_backhands_in_made', 'opp_backhands_in_total')} rawSecondaryData={rawPctSeries('shot_all', 'opp_backhands_in_made', 'opp_backhands_in_total')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Errors — Total" legend={[{ color: '#ef4444', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_all', 'own_total_errors')} rawData={rawSeries('shot_all', 'own_total_errors')} color="#ef4444"
          secondaryData={series('shot_all', 'opp_total_errors')} rawSecondaryData={rawSeries('shot_all', 'opp_total_errors')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Errors — Out" legend={[{ color: '#ef4444', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_all', 'own_errors_out')} rawData={rawSeries('shot_all', 'own_errors_out')} color="#ef4444"
          secondaryData={series('shot_all', 'opp_errors_out')} rawSecondaryData={rawSeries('shot_all', 'opp_errors_out')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Errors — Net" legend={[{ color: '#ef4444', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_all', 'own_errors_net')} rawData={rawSeries('shot_all', 'own_errors_net')} color="#ef4444"
          secondaryData={series('shot_all', 'opp_errors_net')} rawSecondaryData={rawSeries('shot_all', 'opp_errors_net')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
        <Chart title="Avg. Shot Speed (km/h)" legend={[{ color: '#38bdf8', label: 'You' }, { color: '#f97316', label: 'Opponent', dashed: true }]}
          data={series('shot_all', 'own_avg_speed_kmh')} rawData={rawSeries('shot_all', 'own_avg_speed_kmh')}
          secondaryData={series('shot_all', 'opp_avg_speed_kmh')} rawSecondaryData={rawSeries('shot_all', 'opp_avg_speed_kmh')} secondaryColor="#f97316" height={100} hideSecondaryAxis={true} />
      </>}
      </>}
      {/* ── ZOOM MODAL ── */}
      {zoomChart && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 200,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', borderBottom: '1px solid #334155', background: '#1e293b',
          }}>
            <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 'bold' }}>{zoomChart.title}</span>
            <button onClick={() => setZoomChart(null)} style={{
              background: 'none', border: '1px solid #475569', borderRadius: '6px',
              padding: '6px 14px', color: '#e2e8f0', fontSize: '13px', cursor: 'pointer',
            }}>✕ Close</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            <AnalyticsChart {...zoomChart} height={260} dataFilter={dataFilter} />
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────
// SETTINGS VIEW
// ─────────────────────────────────────────────
// ── Searchable sport combobox ─────────────────────────────────────────────────

// ── Multi-sport searchable picker (for Game Plan sports) ──────────────────────

function SettingsView({ playerName, playerId, onPlayerSaved, session, isDemoMode, demoAthlete, userPlan, userPlanStatus, userPlanExpiresAt, onManagePlan, managePlanError, gamePlanSportsList = [], onAddSport }) {
  const googleName = session?.user?.user_metadata?.full_name || session?.user?.user_metadata?.name || '';
  const userId = session?.user?.id;
  const [name, setName] = useState(playerName || googleName);
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [country, setCountry] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // GDPR self-service: export + delete account (2026-10-24)
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const exportMyData = async () => {
    if (!userId) return;
    setExporting(true); setExportError(null);
    try {
      const res = await fetch(`${API}/profiles/${userId}/export`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zenanalytics-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[SettingsView] export failed:', e);
      setExportError('Could not export your data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const deleteMyAccount = async () => {
    if (!userId || deleteConfirmText !== 'DELETE') return;
    setDeleting(true); setDeleteError(null);
    try {
      const res = await fetch(`${API}/profiles/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ user_id: userId, confirm: true }),
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      try { await supabase.auth.signOut(); } catch (_) {}
      window.location.href = '/';
    } catch (e) {
      console.error('[SettingsView] account deletion failed:', e);
      setDeleteError('Could not delete your account. Please try again or contact support@zenanalytics.io.');
      setDeleting(false);
    }
  };

  // Data Coach profile
  const zcAge = (() => {
    if (!birthDate) return '';
    const today = new Date();
    const bd = new Date(birthDate);
    let age = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
    return String(age);
  })();
  const [zcSex, setZcSex] = useState('');
  const [zcSport, setZcSport] = useState('');
  const [zcTimezone, setZcTimezone] = useState(browserTimezone());
  const [zcLocale, setZcLocale] = useState(browserLocale());
  // Country sets sensible defaults for timezone + locale, same as
  // onboarding — both stay separately editable right below it.
  const handleCountryChange = (value) => {
    setCountry(value);
    const defaults = countryDefaults(value);
    if (defaults) {
      setZcTimezone(defaults.timezone);
      setZcLocale(defaults.locale);
    }
  };
  // Lataa profiili kun Settings avataan
  useEffect(() => {
    if (!userId) return;
    const authHeaders = { headers: { Authorization: `Bearer ${session?.access_token}` } };
    axios.get(`${API}/profiles/${userId}`, authHeaders)
      .then(res => {
        const p = res.data;
        if (p.name) setName(p.name);
        if (p.birth_date) setBirthDate(p.birth_date);
        else if (p.birth_year) setBirthDate(`${p.birth_year}-01-01`);
        if (p.gender) setGender(p.gender);
        if (p.country) setCountry(p.country);
      })
      .catch(e => console.error('[SettingsView] profile load failed:', e));
    // Lataa Data Coach-profiili
    axios.get(`${API}/profiles/${userId}/player-profile`, authHeaders)
      .then(res => {
        const p = res.data;
        if (p.biological_sex) setZcSex(p.biological_sex);
        if (p.primary_sport) setZcSport(p.primary_sport.split(',')[0]);
        if (p.timezone) setZcTimezone(p.timezone);
        if (p.locale) setZcLocale(p.locale);
      })
      .catch(e => console.error('[SettingsView] player-profile load failed:', e));
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!name.trim()) return;
    if (birthDate) {
      const year = parseInt(birthDate.slice(0, 4), 10);
      if (year > new Date().getFullYear()) {
        setError('Date of birth cannot be in the future.');
        return;
      }
    }
    setSaving(true); setError(null);
    try {
      // NOTE: this used to POST to `${API}/player` first (a legacy
      // Garmin-era "players" table endpoint) before saving anything else —
      // that route no longer exists on the backend since the Garmin pivot
      // (docs_zenanalytics/00-PIVOT-2026-09.md), so every save silently
      // 404'd and the whole form reported "Save failed. Make sure the
      // backend is running," even though nothing below it was ever wrong.
      // playerId only remains relevant to the unreachable legacy
      // AnalyticsView, so we just keep whatever the app already has
      // locally instead of re-deriving it from a dead endpoint.
      onPlayerSaved(playerId, name.trim());
      if (userId) {
        const profileData = { name: name.trim() };
        if (birthDate) {
          profileData.birth_date = birthDate;
          profileData.birth_year = parseInt(birthDate.slice(0, 4), 10);
        }
        if (gender) profileData.gender = gender;
        if (country) profileData.country = country;
        const authHeaders = { headers: { Authorization: `Bearer ${session?.access_token}` } };
        await axios.patch(`${API}/profiles/${userId}`, profileData, authHeaders);
      }
      // Save Data Coach profile at the same time
      if (userId) {
        const zcData = { user_id: userId };
        if (zcAge) zcData.age = parseInt(zcAge, 10);
        if (zcSex) zcData.biological_sex = zcSex;
        if (zcSport) {
          zcData.primary_sport = zcSport;
          zcData.game_plan_sports = zcSport;
        }
        if (zcTimezone) zcData.timezone = zcTimezone;
        if (zcLocale) zcData.locale = zcLocale;
        // No local .catch() here on purpose — this used to swallow its own
        // failure, so "Saved!" could show even when the sport/timezone/
        // locale half of the save silently failed. Letting it propagate to
        // the outer try/catch means a real failure here now correctly
        // shows "Save failed" instead of a false success (production
        // readiness audit, 2026-10-21).
        await axios.post(`${API}/profiles/player-profile`, zcData, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
      }
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError('Save failed. Make sure the backend is running.');
    }
    setSaving(false);
  };
  // ── Demo read-only profile ────────────────────────────────────────────────
  if (isDemoMode) {
    const a = demoAthlete || {};
    const birthYear = a.age ? new Date().getFullYear() - a.age : '—';
    const demoProfile = [
      { label: 'Name',           value: a.name   || '—' },
      { label: 'Sport',          value: a.sport  || '—' },
      { label: 'Birth year',     value: birthYear },
      { label: 'Gender',         value: a.gender ? (a.gender === 'male' ? 'Man' : a.gender === 'female' ? 'Woman' : a.gender) : '—' },
      { label: 'Activity level', value: a.level  || '—' },
      { label: 'Goals',          value: a.goals?.join(', ') || '—' },
      { label: 'Plan',           value: 'Pro' },
      { label: 'Country',        value: a.flag   || '—' },
    ];
    const roInput = {
      width: '100%', background: '#0a1120', border: '1px solid #1e293b',
      borderRadius: '8px', padding: '10px 14px', color: '#cbd5e1',
      fontSize: '14px', boxSizing: 'border-box', cursor: 'not-allowed',
    };
    const PROFILE_BG = 'https://images.unsplash.com/photo-1707361935112-9c1abbb9ae1f?auto=format&fit=crop&w=1600&q=80';
    return (
      <div style={{ position: 'relative', minHeight: 'calc(100vh - 70px)' }}>
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0,
          backgroundImage: `linear-gradient(180deg, rgba(5,10,20,0.30) 0%, rgba(5,10,20,0.28) 100%), url(${PROFILE_BG})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
      <div style={{ position: 'relative', zIndex: 1, padding: '24px', maxWidth: '500px', margin: '0 auto' }}>
        <h2 style={{ color: '#38bdf8', fontSize: '18px', marginBottom: '16px' }}>⚙️ Profile</h2>
        {/* Info banner */}
        <div style={{
          background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)',
          borderRadius: '10px', padding: '12px 16px', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '18px' }}>👁</span>
          <div>
            <div style={{ color: '#00d4ff', fontSize: '13px', fontWeight: '600' }}>
              You're viewing a demo profile.
            </div>
            <div style={{ color: '#cbd5e1', fontSize: '12px', marginTop: '2px' }}>
              Sign in to create your own.
            </div>
          </div>
        </div>
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <h3 style={{ color: '#e2e8f0', fontSize: '14px', margin: '0 0 16px 0' }}>👤 Player profile</h3>
          {demoProfile.map(({ label, value }) => (
            <div key={label} style={{ marginBottom: '14px' }}>
              <label style={{ color: '#cbd5e1', fontSize: '12px', display: 'block', marginBottom: '5px' }}>{label}</label>
              <input type="text" value={value} readOnly disabled style={roInput} />
            </div>
          ))}
        </div>
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px' }}>
          <h3 style={{ color: '#e2e8f0', fontSize: '14px', margin: '0 0 12px 0' }}>ℹ️ About</h3>
          <p style={{ color: '#cbd5e1', fontSize: '12px', margin: 0 }}>ZenAnalytics · Performance analytics system for racket sport players</p>
          <p style={{ color: '#cbd5e1', fontSize: '12px', margin: '4px 0 0 0' }}>AI performance analytics for racket sport players</p>
        </div>
      </div>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────
  const btnStyle = (active) => ({
    flex: 1,
    padding: '9px 4px',
    fontSize: '13px',
    fontWeight: active ? 'bold' : 'normal',
    borderRadius: '8px',
    border: active ? '1.5px solid #4ade80' : '1.5px solid #334155',
    background: active ? '#052e16' : '#0f172a',
    color: active ? '#4ade80' : '#94a3b8',
    cursor: 'pointer',
    transition: 'all 0.15s',
  });
  const labelStyle = { color: '#d1d5db', fontSize: '12px', display: 'block', marginBottom: '8px', marginTop: '16px' };
  const btnRow = { display: 'flex', gap: '8px', marginBottom: '4px' };
  const PROFILE_BG = 'https://images.unsplash.com/photo-1707361935112-9c1abbb9ae1f?auto=format&fit=crop&w=1600&q=80';
  return (
    <div style={{ position: 'relative', minHeight: 'calc(100vh - 70px)' }}>
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        backgroundImage: `linear-gradient(180deg, rgba(5,10,20,0.30) 0%, rgba(5,10,20,0.28) 100%), url(${PROFILE_BG})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
      }} />
    <div style={{ position: 'relative', zIndex: 1, padding: '24px', maxWidth: '500px', margin: '0 auto' }}>
      <h2 style={{ color: '#38bdf8', fontSize: '18px', marginBottom: '24px' }}>⚙️ Settings</h2>
      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ color: '#e2e8f0', fontSize: '14px', margin: '0 0 16px 0' }}>👤 Profile</h3>
        {/* Name */}
        <label style={{ ...labelStyle, marginTop: 0 }}>Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Enter your name..."
          style={{
            width: '100%', background: '#0f172a', border: '1px solid #334155',
            borderRadius: '8px', padding: '10px 14px', color: '#e2e8f0',
            fontSize: '14px', boxSizing: 'border-box',
          }}
        />
        {/* Email */}
        <label style={labelStyle}>Email</label>
        <input
          type="text"
          value={session?.user?.email || ''}
          readOnly
          style={{
            width: '100%', background: '#0f172a', border: '1px solid #334155',
            borderRadius: '8px', padding: '10px 14px', color: '#cbd5e1',
            fontSize: '14px', boxSizing: 'border-box', cursor: 'default',
          }}
        />
        {/* Birth date */}
        <label style={labelStyle}>Date of birth</label>
        <input
          type="date"
          value={birthDate}
          onChange={e => setBirthDate(e.target.value)}
          min="1920-01-01"
          max="2010-12-31"
          style={{
            width: '100%', background: '#0f172a', border: '1px solid #334155',
            borderRadius: '8px', padding: '10px 14px', color: birthDate ? '#e2e8f0' : '#64748b',
            fontSize: '14px', boxSizing: 'border-box', colorScheme: 'dark',
          }}
        />
        {/* Gender */}
        <label style={labelStyle}>Gender</label>
        <div style={btnRow}>
          {[['male','Male'],['female','Female'],['other','Other']].map(([val, label]) => (
            <button key={val} onClick={() => setGender(gender === val ? '' : val)} style={btnStyle(gender === val)}>{label}</button>
          ))}
        </div>
        {/* Country */}
        <label style={labelStyle}>Country</label>
        <select
          value={country}
          onChange={e => handleCountryChange(e.target.value)}
          style={{
            width: '100%', background: '#0f172a', border: '1px solid #334155',
            borderRadius: '8px', padding: '10px 14px', color: country ? '#e2e8f0' : '#64748b',
            fontSize: '14px', boxSizing: 'border-box', marginBottom: '20px',
          }}
        >
          <option value="">Select country...</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {/* Data Coach Profile — deliberately minimal. Skill level, playing style,
          development focus, coach-expectations and Max HR were removed
          2026-10-01: skill/style are better inferred from actual match data
          than self-reported once, development focus is now what Season
          milestones are for, "what to expect from Coach" is a product/
          character decision we'll make later (not something to ask the
          user to define), and Max HR isn't central without biometric
          tracking. Lighter onboarding beats more fields — see
          docs_zenanalytics/13-PROFILE-SIMPLIFICATION-2026-10-01.md. */}
      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ color: '#00d4ff', fontSize: '14px', margin: '0 0 6px 0' }}>🧬 Profile</h3>
        {zcAge && (
          <p style={{ color: '#475569', fontSize: '11px', margin: '0 0 14px' }}>
            Age derived from birth year: <span style={{ color: '#94a3b8' }}>{zcAge}</span>
          </p>
        )}

        {/* Gender — male/female only, by design: see user feedback 2026-10-01 */}
        <div style={{ marginBottom: '14px' }}>
          <div>
            <label style={{ color: '#cbd5e1', fontSize: '11px', display: 'block', marginBottom: '5px' }}>Gender</label>
            <select value={zcSex} onChange={e => setZcSex(e.target.value)}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: zcSex ? '#e2e8f0' : '#64748b', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>

        {/* Sport — one sport per profile */}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ color: '#cbd5e1', fontSize: '11px', display: 'block', marginBottom: '6px' }}>
            Your sport <span style={{ color: '#475569', fontWeight: 400 }}>— one sport per profile</span>
          </label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {RACKET_SPORTS.map(({ value, label, icon }) => {
              const active = zcSport === value;
              return (
                <button key={value} onClick={() => setZcSport(value)} style={{
                  padding: '5px 11px', borderRadius: '99px', fontSize: '12px',
                  fontWeight: active ? '700' : '400',
                  border: `1px solid ${active ? '#10b981' : '#334155'}`,
                  background: active ? '#10b98118' : 'transparent',
                  color: active ? '#10b981' : '#94a3b8',
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s',
                }}>
                  {icon} {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sports you track — Game Plan/Coach/Equipment data per sport. Adding
            one is deliberately only possible here, not from the header's
            sport switcher: which sports a player tracks may become a
            pricing/plan question later, so it shouldn't be a casual one-click
            action. See user feedback 2026-10-24. */}
        {!isDemoMode && onAddSport && (
          <div style={{ marginBottom: '14px' }}>
            <label style={{ color: '#cbd5e1', fontSize: '11px', display: 'block', marginBottom: '6px' }}>
              Sports you track <span style={{ color: '#475569', fontWeight: 400 }}>— Game Plan, Coach and Equipment data, kept separate per sport</span>
            </label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {RACKET_SPORTS.map(({ value, label, icon }) => {
                const tracked = gamePlanSportsList.includes(value);
                return (
                  <button
                    key={value}
                    onClick={() => !tracked && onAddSport(value)}
                    disabled={tracked}
                    title={tracked ? `Already tracking ${label}` : `Start tracking ${label}`}
                    style={{
                      padding: '5px 11px', borderRadius: '99px', fontSize: '12px',
                      fontWeight: tracked ? '700' : '400',
                      border: `1px solid ${tracked ? '#10b981' : '#334155'}`,
                      background: tracked ? '#10b98118' : 'transparent',
                      color: tracked ? '#10b981' : '#94a3b8',
                      cursor: tracked ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.12s',
                    }}>
                    {icon} {label} {tracked ? '✓' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Timezone — used for "today", dates and period summaries */}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ color: '#cbd5e1', fontSize: '11px', display: 'block', marginBottom: '5px' }}>Timezone</label>
          <select value={zcTimezone} onChange={e => setZcTimezone(e.target.value)}
            style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#e2e8f0', fontSize: '13px', boxSizing: 'border-box' }}>
            {listTimezones().map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>

        {/* Date format — formatting only, the app itself is always English */}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ color: '#cbd5e1', fontSize: '11px', display: 'block', marginBottom: '5px' }}>Date format</label>
          <select value={zcLocale} onChange={e => setZcLocale(e.target.value)}
            style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '9px 12px', color: '#e2e8f0', fontSize: '13px', boxSizing: 'border-box' }}>
            {!listLocales().includes(zcLocale) && <option value={zcLocale}>{localeLabel(zcLocale)}</option>}
            {listLocales().map(l => <option key={l} value={l}>{localeLabel(l)}</option>)}
          </select>
        </div>

      </div>

      {/* Unified save button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={save}
          disabled={!name.trim() || saving}
          style={{
            width: '100%', background: !name.trim() ? '#1e293b' : '#00d4ff',
            color: !name.trim() ? '#475569' : '#060d1a',
            border: 'none', borderRadius: '10px', padding: '14px 24px',
            fontWeight: 'bold', fontSize: '15px',
            cursor: !name.trim() ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
          }}>
          {saving ? 'Saving...' : 'Save profile'}
        </button>
        {saved && (
          <p style={{ color: '#4ade80', fontSize: '13px', textAlign: 'center', marginTop: '10px', fontWeight: 'bold' }}>
            Profile saved ✅
          </p>
        )}
        {error && <p style={{ color: '#fca5a5', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>{error}</p>}
      </div>

      {/* Subscription card */}
      {!isDemoMode && (() => {
        // One product. Legacy plan keys (garmin/insights/endurance/...) are aliases of it.
        const PRODUCT_FEATURES = [
          'Game Plan — prepare and debrief every match',
          'Game Stats — head-to-head, competitions and equipment',
          'Data Coach — an AI coach that reads your plans, results and notes',
        ];
        const PRODUCT = { label: 'ZenAnalytics', price: '€10', color: '#3dbfb0', bg: 'rgba(61,191,176,0.08)', border: 'rgba(61,191,176,0.30)', features: PRODUCT_FEATURES };
        const PM = {
          garmin: PRODUCT, insights: PRODUCT, endurance: PRODUCT, total_coach: PRODUCT,
          sparring: PRODUCT, pro: PRODUCT, base: PRODUCT,
          pilot:          { ...PRODUCT, label: 'Pilot', price: 'Free' },
          pilot_data_coach: { ...PRODUCT, label: 'Pilot', price: 'Free' },
        };
        const m = userPlan ? (PM[userPlan] || null) : null;
        return (
          <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '14px', margin: '0 0 16px 0' }}>💳 Subscription</h3>
            {m ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      color: m.color, background: m.bg, border: `1px solid ${m.border}`,
                      borderRadius: '20px', padding: '3px 12px', fontSize: '12px', fontWeight: '700',
                      letterSpacing: '0.07em', textTransform: 'uppercase',
                    }}>
                      {m.label}
                    </div>
                    <span style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '16px' }}>{m.price}<span style={{ color: '#64748b', fontSize: '12px', fontWeight: '400' }}>/mo</span></span>
                  </div>
                  {userPlanStatus === 'active' && (
                    <span style={{ color: '#4ade80', fontSize: '11px', fontWeight: '700', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: '20px', padding: '2px 9px' }}>Active</span>
                  )}
                </div>
                {userPlanExpiresAt && (
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 12px' }}>
                    Access valid until <b style={{ color: '#cbd5e1' }}>{formatDateLocaleAware(userPlanExpiresAt, { day: 'numeric', month: 'long', year: 'numeric' })}</b>
                  </p>
                )}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {m.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ color: '#4ade80', fontSize: '12px', flexShrink: 0, marginTop: '2px' }}>✓</span>
                      <span style={{ color: '#cbd5e1', fontSize: '13px' }}>{f}</span>
                    </li>
                  ))}
                </ul>
                {userPlanStatus === 'trialing' && (
                  <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px' }}>
                    <span style={{ color: '#10b981', fontSize: '12px', fontWeight: 600 }}>🎯 Free trial — card on file, no charge until trial ends</span>
                  </div>
                )}
                <button
                  onClick={onManagePlan}
                  style={{
                    width: '100%', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)',
                    borderRadius: '10px', padding: '11px 16px', color: '#00d4ff',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.15)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,212,255,0.08)'}
                >
                  {userPlanStatus === 'trialing' ? 'Manage trial →' : 'Manage subscription →'}
                </button>
                <p style={{ color: '#475569', fontSize: '11px', margin: '8px 0 0', lineHeight: 1.6, textAlign: 'center' }}>
                  {userPlanStatus === 'trialing'
                    ? 'Cancel anytime before the trial ends — handled securely by Stripe.'
                    : 'Cancel or update your payment method — handled securely by Stripe.'}
                </p>
                {managePlanError && (
                  <p style={{ color: '#f87171', fontSize: '12px', margin: '8px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
                    ⚠️ {managePlanError}
                  </p>
                )}
              </>
            ) : (
              <>
                <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 16px', lineHeight: 1.6 }}>
                  No active subscription. Start your free trial to unlock Game Plan, Game Stats and Data Coach.
                </p>
                <button
                  onClick={() => window.location.href = '/?subscribe=true'}
                  style={{
                    width: '100%', background: '#0ea5e9', border: 'none',
                    borderRadius: '10px', padding: '11px 16px', color: '#fff',
                    fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0284c7'}
                  onMouseLeave={e => e.currentTarget.style.background = '#0ea5e9'}
                >
                  View plans →
                </button>
              </>
            )}
          </div>
        );
      })()}

      {!isDemoMode && userId && (
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <h3 style={{ color: '#e2e8f0', fontSize: '14px', margin: '0 0 6px 0' }}>📦 Your data</h3>
          <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 14px', lineHeight: 1.6 }}>
            Download everything ZenAnalytics has stored about you — every game plan, result,
            competition, equipment item and note — as one file.
          </p>
          <button
            onClick={exportMyData}
            disabled={exporting}
            style={{
              width: '100%', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)',
              borderRadius: '10px', padding: '11px 16px', color: '#00d4ff',
              fontSize: '13px', fontWeight: '600', cursor: exporting ? 'default' : 'pointer',
              fontFamily: 'inherit', opacity: exporting ? 0.6 : 1,
            }}
          >
            {exporting ? 'Preparing export…' : 'Download my data →'}
          </button>
          {exportError && (
            <p style={{ color: '#f87171', fontSize: '12px', margin: '8px 0 0', textAlign: 'center' }}>⚠️ {exportError}</p>
          )}
        </div>
      )}

      {!isDemoMode && userId && (
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px', border: '1px solid rgba(239,68,68,0.25)' }}>
          <h3 style={{ color: '#f87171', fontSize: '14px', margin: '0 0 6px 0' }}>⚠️ Danger zone</h3>
          <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 14px', lineHeight: 1.6 }}>
            Permanently delete your account and all your data. This cancels any active
            subscription and cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmText(''); setDeleteError(null); }}
              style={{
                width: '100%', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '10px', padding: '11px 16px', color: '#f87171',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Delete my account…
            </button>
          ) : (
            <div>
              <p style={{ color: '#cbd5e1', fontSize: '12px', margin: '0 0 8px', lineHeight: 1.6 }}>
                Type <b style={{ color: '#f87171' }}>DELETE</b> to confirm. This is permanent.
              </p>
              <input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: '100%', boxSizing: 'border-box', background: '#0f172a',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px',
                  padding: '9px 12px', color: '#e2e8f0', fontSize: '13px',
                  fontFamily: 'inherit', marginBottom: '10px',
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    flex: 1, background: 'transparent', border: '1px solid #334155',
                    borderRadius: '8px', padding: '10px', color: '#94a3b8',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={deleteMyAccount}
                  disabled={deleteConfirmText !== 'DELETE' || deleting}
                  style={{
                    flex: 1, background: deleteConfirmText === 'DELETE' ? '#ef4444' : 'rgba(239,68,68,0.2)',
                    border: 'none', borderRadius: '8px', padding: '10px', color: '#fff',
                    fontSize: '13px', fontWeight: '700',
                    cursor: deleteConfirmText === 'DELETE' && !deleting ? 'pointer' : 'default',
                    fontFamily: 'inherit', opacity: deleting ? 0.7 : 1,
                  }}
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
              </div>
              {deleteError && (
                <p style={{ color: '#f87171', fontSize: '12px', margin: '10px 0 0', textAlign: 'center' }}>⚠️ {deleteError}</p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px' }}>
        <h3 style={{ color: '#e2e8f0', fontSize: '14px', margin: '0 0 12px 0' }}>ℹ️ About</h3>
        <p style={{ color: '#cbd5e1', fontSize: '12px', margin: 0 }}>ZenAnalytics · Performance analytics system for racket sport players</p>
        <p style={{ color: '#cbd5e1', fontSize: '12px', margin: '4px 0 0 0' }}>AI performance analytics for racket sport players</p>
      </div>
    </div>
    </div>
  );
}
function HelpP({ children }) {
  return <p style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: '1.7', margin: '0 0 10px' }}>{children}</p>;
}
function HelpContent({ section }) {
  const T = { color: '#38bdf8', fontSize: '18px', margin: '0 0 14px', fontWeight: 'bold' };
  switch (section) {
    case 'overview': return (
      <div><h2 style={T}>Yleiskatsaus</h2><HelpP>Ohjesisältö tulossa pian.</HelpP></div>
    );
    case 'dashboard': return (
      <div><h2 style={T}>Dashboard</h2><HelpP>Ohjesisältö tulossa pian.</HelpP></div>
    );
    case 'reports': return (
      <div><h2 style={T}>Raportit</h2><HelpP>Ohjesisältö tulossa pian.</HelpP></div>
    );
    case 'ai': return (
      <div><h2 style={T}>AI Analytics</h2><HelpP>Ohjesisältö tulossa pian.</HelpP></div>
    );
    case 'settings': return (
      <div><h2 style={T}>Asetukset</h2><HelpP>Ohjesisältö tulossa pian.</HelpP></div>
    );
    case 'faq': return (
      <div><h2 style={T}>UKK</h2><HelpP>Ohjesisältö tulossa pian.</HelpP></div>
    );
    default: return null;
  }
}
function HelpContentEN({ section }) {
  const T = { color: '#38bdf8', fontSize: '18px', margin: '0 0 14px', fontWeight: 'bold' };
  switch (section) {
    case 'overview': return (
      <div><h2 style={T}>Overview</h2><HelpP>Help content coming soon.</HelpP></div>
    );
    case 'dashboard': return (
      <div><h2 style={T}>Dashboard</h2><HelpP>Help content coming soon.</HelpP></div>
    );
    case 'reports': return (
      <div><h2 style={T}>Reports</h2><HelpP>Help content coming soon.</HelpP></div>
    );
    case 'ai': return (
      <div><h2 style={T}>AI Analytics</h2><HelpP>Help content coming soon.</HelpP></div>
    );
    case 'settings': return (
      <div><h2 style={T}>Settings</h2><HelpP>Help content coming soon.</HelpP></div>
    );
    case 'faq': return (
      <div><h2 style={T}>FAQ</h2><HelpP>Help content coming soon.</HelpP></div>
    );
    default: return null;
  }
}
function HelpModal({ onClose, initialSection }) {
  const [section, setSection] = useState(initialSection || 'overview');
  const [lang, setLang] = useState('en');
  const sections = lang === 'fi'
    ? [
        { id: 'overview', label: 'Yleiskatsaus' },
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'reports', label: 'Raportit' },
        { id: 'ai', label: 'AI Analytics' },
        { id: 'settings', label: 'Asetukset' },
        { id: 'faq', label: 'UKK' },
      ]
    : [
        { id: 'overview', label: 'Overview' },
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'reports', label: 'Reports' },
        { id: 'ai', label: 'AI Analytics' },
        { id: 'settings', label: 'Settings' },
        { id: 'faq', label: 'FAQ' },
      ];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}
      onClick={onClose}>
      <div style={{ background: '#0f172a', width: '100%', height: '92vh', display: 'flex', flexDirection: 'column', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: '#1e293b', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #334155', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>📖</span>
            <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: '17px' }}>{lang === 'fi' ? 'Ohjekirja' : 'Help Guide'}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Language toggle */}
            <div style={{ display: 'flex', gap: '2px', background: '#0f172a', borderRadius: '6px', padding: '2px' }}>
              {['en', 'fi'].map(l => (
                <button key={l} onClick={() => setLang(l)} style={{
                  background: lang === l ? '#38bdf8' : 'none',
                  color: lang === l ? '#0f172a' : '#64748b',
                  border: 'none', borderRadius: '4px', padding: '3px 9px',
                  fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', textTransform: 'uppercase',
                }}>{l}</button>
              ))}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '22px', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>✕</button>
          </div>
        </div>
        {/* Section tabs — horizontal scroll */}
        <div style={{ display: 'flex', overflowX: 'auto', background: '#1e293b', borderBottom: '1px solid #334155', flexShrink: 0, scrollbarWidth: 'none' }}>
          {sections.map((s, i) => (
            <button key={s.id} onClick={() => setSection(s.id)} style={{
              flexShrink: 0, background: 'none', border: 'none',
              borderBottom: section === s.id ? '2px solid #38bdf8' : '2px solid transparent',
              color: section === s.id ? '#38bdf8' : '#64748b',
              padding: '10px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap',
            }}>
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {lang === 'fi' ? <HelpContent section={section} /> : <HelpContentEN section={section} />}
        </div>
      </div>
    </div>
  );
}
// BOTTOM NAV
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// TRAINING HISTORY VIEW
// ─────────────────────────────────────────────
const SPORT_COLOR = {
  running:       '#3b82f6', cycling:      '#f59e0b', strength:    '#ef4444',
  swimming:      '#06b6d4', tennis:       '#a78bfa', hiking:      '#22c55e',
  skiing:        '#38bdf8', yoga:         '#f472b6', other:       '#64748b',
  triathlon:     '#818cf8', trail_running:'#34d399', rowing:      '#60a5fa',
  football:      '#4ade80', basketball:   '#fb923c', padel:       '#c084fc',
  powerlifting:  '#f87171', crossfit:     '#fbbf24', hiit:        '#fb923c',
  boxing:        '#f87171', martial_arts: '#f87171', climbing:    '#22c55e',
  snowboarding:  '#38bdf8', volleyball:   '#4ade80', ice_hockey:  '#67e8f9',
  gym:           '#ef4444', // legacy alias → strength
};
function sportColor(t) { return SPORT_COLOR[t?.toLowerCase()] ?? SPORT_COLOR.other; }

const PERIODS = [
  { key: '1M',  label: '1M',  days: 30  },
  { key: '3M',  label: '3M',  days: 90  },
  { key: '6M',  label: '6M',  days: 180 },
  { key: '1Y',  label: '1Y',  days: 365 },
  { key: '2Y',  label: '2Y',  days: 730 },
  { key: 'custom', label: 'Custom', days: null },
];

// Matches Analytics view pill style exactly
const histPill = (active) => ({
  padding: '6px 14px', borderRadius: '20px', fontSize: '13px',
  border: `1px solid ${active ? '#00d4ff' : '#334155'}`,
  background: active ? '#00d4ff22' : 'transparent',
  color: active ? '#00d4ff' : '#cbd5e1',
  cursor: 'pointer', fontWeight: active ? 600 : 400,
  transition: 'all 0.15s', fontFamily: 'inherit',
});




// ── 30-day activity summary ───────────────────────────────────────────────────



// ── Game Plan Summary widget for Home screen ──────────────────────────────────

function BottomNav({ active, onChange, hideTabs = [] }) {
  const allTabs = [
    { id: 'home',            icon: '🏠', label: 'Home' },
    { id: 'session_reports', icon: '📊', label: 'Game analytics' },
    { id: 'program',         icon: '🪄', label: 'Create & Manage' },
    { id: 'chat',            icon: '💬', label: 'Data Coach' },
  ];
  const tabs = allTabs.filter(t => !hideTabs.includes(t.id));
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: '#1e293b', borderTop: '1px solid rgba(56,189,248,0.45)',
      display: 'flex', justifyContent: 'center',
      zIndex: 100,
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {/* Thin dividers between items only (no outer box) — same "internal
          gridlines, no outer edge" look as Home's Summary stat grid, per
          user feedback 2026-10-02. */}
      <div style={{ display: 'flex', width: '100%', maxWidth: '640px' }}>
      {tabs.map((tab, i) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            flex: 1, background: 'none',
            border: 'none',
            borderLeft: i > 0 ? '1px solid rgba(56,189,248,0.25)' : 'none',
            padding: '10px 0 8px', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
          }}>
          {tab.iconComponent
            ? (() => { const Icon = tab.iconComponent; return <Icon size={22} color={active === tab.id ? '#00f0ff' : '#00b8cc'} />; })()
            : <span style={{ fontSize: '22px' }}>{tab.icon}</span>
          }
          <span style={{
            fontSize: tab.label.length > 10 ? '8.5px' : '10px', fontWeight: 'bold',
            whiteSpace: 'nowrap',
            color: active === tab.id
              ? (tab.id === 'coach' ? '#00f0ff' : '#38bdf8')
              : (tab.id === 'coach' ? '#00b8cc' : '#cbd5e1'),
          }}>{tab.label}</span>
          {active === tab.id && (
            <div style={{ width: '20px', height: '2px', background: '#38bdf8', borderRadius: '1px' }} />
          )}
        </button>
      ))}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────
// NOT AUTHORIZED VIEW
// ─────────────────────────────────────────────
function NotAuthorizedPage() {
  const navigate = useNavigate();
  return (
    <div style={{
      minHeight: '100vh', background: '#0a0f1a', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px', fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ maxWidth: '480px', textAlign: 'center' }}>
        <div style={{ fontSize: '40px', marginBottom: '24px' }}>⌚</div>
        <h1 style={{ color: '#e2e8f0', fontSize: '22px', fontWeight: '700', margin: '0 0 16px' }}>
          ZenAnalytics
        </h1>
        <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.7, margin: '0 0 32px' }}>
          ZenAnalytics is a paid product. Sign in to access your account.
        </p>
        <button
          onClick={triggerGoogleSignIn}
          style={{
            background: '#0ea5e9', color: '#ffffff', border: 'none',
            borderRadius: '8px', padding: '12px 28px',
            fontSize: '15px', fontWeight: '700', cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Sign in →
        </button>
      </div>
    </div>
  );
}
// LOGIN VIEW
// ─────────────────────────────────────────────
function LoginView() {
  const [loadingG, setLoadingG] = useState(false);
  const [loadingM, setLoadingM] = useState(false);

  const handleGoogleSignIn = async () => {
    setLoadingG(true);
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  };
  const handleMicrosoftSignIn = async () => {
    setLoadingM(true);
    await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: { redirectTo: window.location.origin, scopes: 'email' },
    });
  };

  const font = "'Inter', system-ui, sans-serif";
  const border = '#1a2d47';

  const BG = 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=1920&q=80';

  return (
    <div style={{
      minHeight: '100vh', fontFamily: font,
      position: 'relative', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px 20px',
    }}>
      {/* Background image */}
      <div style={{
        position: 'fixed', inset: 0,
        backgroundImage: `linear-gradient(135deg, rgba(6,13,26,0.35) 0%, rgba(6,13,26,0.12) 50%, rgba(6,13,26,0.28) 100%), url(${BG})`,
        backgroundSize: 'cover', backgroundPosition: 'center 40%',
        backgroundRepeat: 'no-repeat',
        filter: 'saturate(1.4) brightness(1.15)',
        zIndex: 0,
      }} />

      {/* Glassmorphism card */}
      <div style={{
        position: 'relative', zIndex: 2,
        background: 'rgba(13,24,41,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: '24px',
        padding: '48px 40px',
        width: '100%', maxWidth: '400px',
        textAlign: 'center',
        boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(14,165,233,0.08)',
      }}>
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>⌚</div>
        <h1 style={{ color: '#eef2f7', fontSize: '24px', fontWeight: '800', margin: '0 0 6px', letterSpacing: '-0.5px' }}>ZenAnalytics</h1>
        <p style={{ color: '#a0bdd0', fontSize: '13px', margin: '0 0 36px' }}>Performance analytics system for racket sport players</p>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ color: '#a0bdd0', fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sign in</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Google */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loadingG}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
              background: '#ffffff', color: '#3c4043',
              border: 'none', borderRadius: '12px',
              padding: '14px 20px', fontSize: '15px', fontWeight: '600',
              cursor: loadingG ? 'not-allowed' : 'pointer',
              opacity: loadingG ? 0.7 : 1,
              fontFamily: font,
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
              transition: 'transform 0.1s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { if (!loadingG) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.4)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.3)'; }}>
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/><path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
            {loadingG ? 'Redirecting…' : 'Continue with Google'}
          </button>

          {/* Microsoft */}
          <button
            onClick={handleMicrosoftSignIn}
            disabled={loadingM}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
              background: 'rgba(255,255,255,0.06)', color: '#ddeaf5',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px',
              padding: '14px 20px', fontSize: '15px', fontWeight: '600',
              cursor: loadingM ? 'not-allowed' : 'pointer',
              opacity: loadingM ? 0.7 : 1,
              fontFamily: font,
              transition: 'transform 0.1s, background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { if (!loadingM) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgba(47,110,212,0.15)'; e.currentTarget.style.borderColor = 'rgba(47,110,212,0.4)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
            <svg width="18" height="18" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
            {loadingM ? 'Redirecting…' : 'Continue with Microsoft'}
          </button>
        </div>
      </div>
    </div>
  );
}
/* ─── Sport switcher — active sport shown top-right, switch between
   sports the player already tracks. Adding a NEW sport is deliberately
   not possible from here — it only happens from Settings (see
   SettingsView's "Sports you track" section) — because which sports a
   player tracks may become a pricing/plan question later, and that
   decision shouldn't be one click away from a casual header dropdown.
   See user feedback 2026-10-24. "One sport per profile" was never a real
   limit: profiles.game_plan_sports is already a comma-separated field
   and GamePlanTab's sport dropdown already reads every value in it — see
   docs_zenanalytics/PRODUCT-GUIDELINES.md §5. */
function SportSwitcher({ sports, activeSport, onSwitch }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!sports || sports.length === 0) return null;

  const meta = (value) => RACKET_SPORTS.find(s => s.value === value) || { label: value, icon: '🏅' };
  const active = meta(activeSport || sports[0]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'none', border: '1px solid #334155', borderRadius: '8px',
        cursor: 'pointer', padding: '5px 10px', color: '#e2e8f0', fontSize: '13px',
        display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit',
      }} title="Active sport">
        <span>{active.icon}</span>
        <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{active.label}</span>
        <span style={{ fontSize: '10px', color: '#cbd5e1' }}>{open ? '▲' : '▾'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#0f172a', border: '1px solid #1e293b',
          borderRadius: '10px', minWidth: '200px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)', zIndex: 200,
          overflow: 'hidden',
        }}>
          {sports.map(value => {
            const m = meta(value);
            const isActive = value === (activeSport || sports[0]);
            return (
              <button key={value} onClick={() => { onSwitch(value); setOpen(false); }} style={{
                width: '100%', background: isActive ? 'rgba(56,189,248,0.08)' : 'none',
                border: 'none', padding: '10px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
                fontFamily: 'inherit',
              }}>
                <span style={{ fontSize: '14px' }}>{m.icon}</span>
                <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 600 }}>{m.label}</span>
                {isActive && <span style={{ marginLeft: 'auto', color: '#38bdf8', fontSize: '12px' }}>✓</span>}
              </button>
            );
          })}
          <div style={{ padding: '8px 14px', color: '#475569', fontSize: '10.5px', lineHeight: 1.5, borderTop: '1px solid #1e293b' }}>
            {sports.length > 1 ? `Tracking ${sports.length} sports — data stays separate per sport.` : 'Add more sports from Settings.'}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── User dropdown ──────────────────────────────────────────────── */
function UserDropdown({ playerName, demoPlayers, selectedDemo, onSelectDemo, onSignOut, isDemoGuest, onSignIn, onManagePlan, onGoToProfile, onGiveFeedback, userPlan, userPlanStatus }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const displayName = isDemoGuest ? (playerName || 'Alex Johnsson') : (selectedDemo ? selectedDemo.name : (playerName || 'My Profile'));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'none', border: '1px solid #334155', borderRadius: '8px',
        cursor: 'pointer', padding: '5px 12px', color: '#e2e8f0', fontSize: '13px',
        display: 'flex', alignItems: 'center', gap: '7px',
      }}>
        <span style={{ color: isDemoGuest ? '#fb923c' : (selectedDemo ? '#00d4ff' : '#94a3b8') }}>
          {isDemoGuest ? '👁' : '👤'}
        </span>
        <span className="user-dropdown-name" style={{ color: isDemoGuest ? '#fb923c' : (selectedDemo ? '#00d4ff' : '#e2e8f0'), fontWeight: isDemoGuest ? '600' : (selectedDemo ? '600' : '400') }}>
          {displayName}
        </span>
        {isDemoGuest && <span style={{ fontSize: '10px', background: 'rgba(251,146,60,0.15)', color: '#fb923c', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>DEMO</span>}
        <span style={{ fontSize: '10px', color: '#cbd5e1' }}>{open ? '▲' : '▾'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#0f172a', border: '1px solid #1e293b',
          borderRadius: '10px', minWidth: '220px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)', zIndex: 200,
          overflow: 'hidden',
        }}>
          {isDemoGuest ? (
            /* Demo guest panel */
            <div style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>👁</span>
                <div>
                  <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 700 }}>{displayName}</div>
                  <div style={{ color: '#fb923c', fontSize: '11px', fontWeight: 600 }}>Demo profile — guest view</div>
                </div>
              </div>
              <a href="/" style={{
                display: 'block', textAlign: 'center',
                background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)',
                color: '#00d4ff', borderRadius: 8, padding: '8px 0',
                fontSize: '12px', fontWeight: 700, textDecoration: 'none', marginBottom: 6,
              }}>Create your account →</a>
              <button onClick={() => { onSignOut?.(); }} style={{
                display: 'block', width: '100%', textAlign: 'center',
                background: 'transparent', border: '1px solid #1e293b',
                color: '#8899aa', borderRadius: 8, padding: '7px 0',
                fontSize: '12px', cursor: 'pointer', boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}>← Back to home page</button>
            </div>
          ) : (
            <>
              {/* Own profile */}
              <button onClick={() => { onSelectDemo(null); setOpen(false); if (onGoToProfile) onGoToProfile(); }} style={{
                width: '100%', background: !selectedDemo ? 'rgba(56,189,248,0.08)' : 'none',
                border: 'none', padding: '11px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
              }}>
                <span style={{ fontSize: '14px' }}>👤</span>
                <div>
                  <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600', display: 'block' }}>
                    {playerName || 'My Profile'}
                  </span>
                  <span style={{ color: '#cbd5e1', fontSize: '11px' }}>Your profile</span>
                </div>
                {!selectedDemo && <span style={{ marginLeft: 'auto', color: '#38bdf8', fontSize: '12px' }}>✓</span>}
              </button>
              <div style={{ borderTop: '1px solid #1e293b', margin: '2px 0' }} />
              {/* Manage / upgrade subscription */}
              {userPlan && onManagePlan && (
                <button onClick={() => { setOpen(false); onManagePlan(); }} style={{
                  width: '100%', background: 'none', border: 'none',
                  padding: '10px 14px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = userPlanStatus === 'active' ? 'rgba(0,212,255,0.07)' : 'rgba(16,185,129,0.07)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <span style={{ fontSize: '14px' }}>💳</span>
                  <div>
                    <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600', display: 'block' }}>
                      {userPlanStatus === 'trialing' ? 'Manage trial / upgrade' : 'Manage subscription'}
                    </span>
                    <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {userPlanStatus === 'trialing' ? 'Trial active' : ({'insights': 'Insights', 'endurance': 'Endurance', 'pilot': 'Free Pilot', 'pilot_data_coach': 'Data Coach Pilot'}[userPlan] || userPlan)}
                    </span>
                  </div>
                </button>
              )}
              {/* Demo players — only when list is non-empty */}
              {demoPlayers.length > 0 && (<>
                <div style={{ borderTop: '1px solid #1e293b', margin: '2px 0' }} />
                <div style={{ padding: '6px 14px 4px' }}>
                  <span style={{ color: '#00d4ff', fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    Demo Players
                  </span>
                </div>
                {demoPlayers.map(p => (
                  <button key={p.id} onClick={() => { onSelectDemo(p); setOpen(false); }} style={{
                    width: '100%', background: selectedDemo?.id === p.id ? 'rgba(0,212,255,0.08)' : 'none',
                    border: 'none', padding: '10px 14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = selectedDemo?.id === p.id ? 'rgba(0,212,255,0.08)' : 'none'}
                  >
                    <span style={{ fontSize: '14px' }}>🎾</span>
                    <span style={{ color: '#e2e8f0', fontSize: '13px', flex: 1 }}>{p.name}</span>
                    <span style={{
                      background: 'rgba(0,212,255,0.12)', color: '#00d4ff',
                      fontSize: '9px', fontWeight: '700', borderRadius: '4px',
                      padding: '2px 6px', letterSpacing: '0.08em',
                    }}>DEMO</span>
                    {selectedDemo?.id === p.id && <span style={{ color: '#00d4ff', fontSize: '12px' }}>✓</span>}
                  </button>
                ))}
              </>)}
              {/* Give feedback — moved here from its own standalone header
                  button (feedback 2026-11-02: it read as oddly detached,
                  floating alone in the nav bar). Lives with the rest of
                  the account menu instead. */}
              {onGiveFeedback && (
                <button type="button" onClick={() => { setOpen(false); onGiveFeedback(); }} style={{
                  width: '100%', background: 'none', border: 'none',
                  padding: '10px 14px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
                  color: '#e2e8f0', fontSize: '13px', fontWeight: 600,
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(56,189,248,0.07)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <span style={{ fontSize: '14px' }}>📝</span> Give feedback
                </button>
              )}
              <div style={{ borderTop: '1px solid #1e293b', margin: '4px 0' }} />
              {/* Sign out */}
              <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(false); onSignOut(); }} style={{
                width: '100%', background: 'none', border: 'none',
                padding: '10px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
                color: '#cbd5e1', fontSize: '13px',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <span>🚪</span> Sign out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Palautteenkeräys — kevennetty siirto Wellflixin "Give feedback" -mekanismista
// (kartoitus 2026-10-21). Thin header-button opens a bottom-sheet modal;
// FeedbackWidget owns all its own state so it can be mounted once in the
// header without threading open/close state through the rest of App.js.
const FEEDBACK_TYPES = [
  { key: 'bugi', label: 'Report a bug', icon: '🐛' },
  { key: 'kehitysehdotus', label: 'Propose idea', icon: '💡' },
  { key: 'muu', label: 'Other', icon: '💬' },
];
const FEEDBACK_CATEGORIES = [
  { key: 'home', label: 'Home' },
  { key: 'game_plan', label: 'Game Plan' },
  { key: 'data_coach', label: 'Data Coach' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'stats', label: 'Game analytics' },
  { key: 'settings', label: 'Settings' },
  { key: 'muu', label: 'Other' },
];
const TAB_TO_FEEDBACK_CATEGORY = {
  home: 'home', session_reports: 'stats', data_lab: 'stats',
  equipment: 'equipment', coach: 'data_coach', settings: 'settings',
};

// `openTrigger` is a counter bumped by the "Give feedback" item in the My
// Profile dropdown (same counter-trigger idiom as tutorialModalCloseTrigger
// elsewhere in this file) — this widget no longer owns a visible button of
// its own, just the modal. See feedback 2026-11-02: the standalone header
// button sat oddly alone in the nav; it belongs with the rest of the
// account menu instead.
function FeedbackWidget({ session, activeTab, openTrigger }) {
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('muu');
  const [category, setCategory] = useState('muu');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const openModal = useCallback(() => {
    setCategory(TAB_TO_FEEDBACK_CATEGORY[activeTab] || 'muu');
    setFeedbackType('muu'); setMessage(''); setImageFile(null); setImagePreview(null);
    setError(null); setDone(false); setOpen(true);
  }, [activeTab]);

  const firstTriggerRef = useRef(true);
  useEffect(() => {
    if (firstTriggerRef.current) { firstTriggerRef.current = false; return; }
    openModal();
  }, [openTrigger, openModal]);

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError('Image is too large (max 8MB)'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const submit = async () => {
    if (!message.trim()) { setError('Write a message'); return; }
    setSending(true); setError(null);
    try {
      const authHeaders = { Authorization: `Bearer ${session?.access_token}` };
      let image_url = '';
      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        const res = await fetch(`${API}/feedback/upload-image`, { method: 'POST', headers: authHeaders, body: fd });
        if (!res.ok) throw new Error('Image upload failed');
        image_url = (await res.json()).image_url || '';
      }
      const body = new FormData();
      body.append('page', activeTab || '');
      body.append('feedback_type', feedbackType);
      body.append('category', category);
      body.append('message', message.trim());
      if (image_url) body.append('image_url', image_url);
      const res = await fetch(`${API}/feedback`, { method: 'POST', headers: authHeaders, body });
      if (!res.ok) throw new Error('Feedback submission failed');
      setDone(true);
    } catch (e) {
      console.error('[FeedbackWidget] submit failed:', e);
      setError(e.message || 'Something went wrong');
    } finally {
      setSending(false);
    }
  };

  if (!session || !open) return null;

  return (
    <div onClick={() => !sending && setOpen(false)} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)', borderRadius: 16,
        padding: 24, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', fontFamily: 'inherit',
      }}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Thanks for the feedback!</div>
            <button onClick={() => setOpen(false)} style={{ background: '#0ea5e9', border: 'none', borderRadius: 8, color: '#fff', padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 700 }}>Give feedback</div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#8899aa', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              {FEEDBACK_TYPES.map(t => (
                <button key={t.key} onClick={() => setFeedbackType(t.key)} style={{
                  padding: '7px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${feedbackType === t.key ? '#0ea5e9' : '#334155'}`,
                  background: feedbackType === t.key ? 'rgba(14,165,233,0.15)' : 'transparent',
                  color: feedbackType === t.key ? '#38bdf8' : '#94a3b8',
                }}>{t.icon} {t.label}</button>
              ))}
            </div>
            <select value={category} onChange={e => setCategory(e.target.value)} style={{
              width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #334155',
              background: '#0f172a', color: '#e2e8f0', fontSize: 13, marginBottom: 12,
            }}>
              {FEEDBACK_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
              placeholder="Tell us what you noticed…" style={{
                width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #334155',
                background: '#0f172a', color: '#e2e8f0', fontSize: 13, resize: 'vertical', marginBottom: 12,
                fontFamily: 'inherit', boxSizing: 'border-box',
              }} />
            {imagePreview && (
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}>
                <img src={imagePreview} alt="Attachment" style={{ maxWidth: 140, borderRadius: 8, display: 'block' }} />
                <button onClick={() => { setImageFile(null); setImagePreview(null); }} style={{
                  position: 'absolute', top: -8, right: -8, background: '#ef4444', border: 'none',
                  borderRadius: '50%', width: 22, height: 22, color: '#fff', cursor: 'pointer', fontSize: 13,
                }}>×</button>
              </div>
            )}
            <label style={{ display: 'inline-block', fontSize: 12, color: '#38bdf8', cursor: 'pointer', marginBottom: 14 }}>
              📎 Attach image
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickImage} style={{ display: 'none' }} />
            </label>
            {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            <button onClick={submit} disabled={sending || !message.trim()} style={{
              width: '100%', background: '#0ea5e9', border: 'none', borderRadius: 8, color: '#fff',
              padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: sending ? 0.6 : 1,
            }}>{sending ? 'Sending…' : 'Send feedback'}</button>
          </>
        )}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────
export default function App() {
  // Compute isDemoMode before any useState so it can be used in initialisers
  const isDemoMode = new URLSearchParams(window.location.search).get('demo') === 'true';
  const [session, setSession] = useState(undefined); // undefined = loading, null = logged out
  const [demoModePlayer, setDemoModePlayer] = useState(null);   // Alex Chen — fetched when ?demo=true
  const [demoLoadError,  setDemoLoadError]  = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [feedbackOpenTrigger, setFeedbackOpenTrigger] = useState(0); // bumped by "Give feedback" in the My Profile dropdown
  const [dataLabView, setDataLabView] = useState('charts'); // 'charts' | 'calendar' | 'reports'
  const switchTab = (tab) => { setActiveTab(tab); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const [playerId, setPlayerId] = useState(() => localStorage.getItem('zd_player_id') || null);
  const [playerName, setPlayerName] = useState(() => localStorage.getItem('zd_player_name') || '');
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState('overview');
  const [primarySport, setPrimarySport] = useState(null); // loaded from the player profile — the "active sport" shown top-right
  // Fixed 2026-10-28 (activeSport race): primarySport starts null and only
  // resolves once the player-profile fetch below returns. Home/Create &
  // Manage/Data Coach used to mount immediately on `session` alone, passing
  // activeSport={null} — and useSportFiltered (sportScope.js) deliberately
  // returns items UNFILTERED when activeSport is falsy, so every sport's
  // data rendered unscoped for one visible beat before the real sport
  // arrived and the correct, filtered view replaced it. Gating those tabs
  // on primarySportLoaded instead of just `session` closes the window
  // entirely rather than just narrowing it.
  const [primarySportLoaded, setPrimarySportLoaded] = useState(false);
  const [gamePlanSportsList, setGamePlanSportsList] = useState([]); // every sport the player tracks (profiles.game_plan_sports, comma-separated)
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showPilotWelcome, setShowPilotWelcome] = useState(null); // null | 'first' | 'return'
  const [showPortalReturn, setShowPortalReturn] = useState(false);
  const [userPlan, setUserPlan] = useState(null);         // active plan key (legacy aliases of the single product) or null
  const [userPlanStatus, setUserPlanStatus] = useState(null);
  const [userSource, setUserSource] = useState(null);    // 'manual_pilot' | null
  const [userPlanExpiresAt, setUserPlanExpiresAt] = useState(null); // subscriptions.current_period_end
  const [paymentFailedAt, setPaymentFailedAt] = useState(null);
  const [deletionScheduledAt, setDeletionScheduledAt] = useState(null);
  const [pendingOpenPlan, setPendingOpenPlan] = useState(null); // auto-open SessionModal in GamePlanTab
  const [pendingOpenCompetition, setPendingOpenCompetition] = useState(null); // auto-open a competition's edit form in GamePlanTab, from Home's Season module
  const [tutorialModalCloseTrigger, setTutorialModalCloseTrigger] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [demoPlayers,  setDemoPlayers]    = useState([]);
  const [selectedDemo, setSelectedDemo]   = useState(null); // null = own profile
  const [demoProfileIdx, setDemoProfileIdx] = useState(() => {
    const profileKey = new URLSearchParams(window.location.search).get('profile');
    if (profileKey) {
      const idx = DEMO_ATHLETES.findIndex(a => a.key === profileKey);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });  // which of 8 demo athletes is active
  const activeDemoData = null;
  // Handle ?checkout=success return from Stripe
  // NOTE: do NOT navigate away here — keeping ?checkout=success in the URL ensures
  // onAuthStateChange sees isStripeReturn=true and skips the subscription check,

  // preventing a race condition where a missing webhook would redirect to /onboarding/plan.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('checkout') === 'success') {
      setCheckoutSuccess(true);
      setShowWelcome(true);
      window.scrollTo(0, 0);
    }
    if (new URLSearchParams(location.search).get('portal_return') === 'true') {
      setShowPortalReturn(true);
      window.scrollTo(0, 0);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sync demoProfileIdx with URL ?profile= param on every navigation (SPA doesn't remount)
  useEffect(() => {
    const profileKey = new URLSearchParams(location.search).get('profile');
    if (profileKey) {
      const idx = DEMO_ATHLETES.findIndex(a => a.key === profileKey);
      if (idx >= 0) setDemoProfileIdx(idx);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // GA: init immediately if already consented (catches return visits)
  useEffect(() => { initGAIfConsented(); }, []);
  // GA: track page views on route changes
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: location.pathname });
    }
  }, [location.pathname]);
  // Analytics refs (demo mode)
  const demoSessionStart  = useRef(Date.now());
  const demoTabStart      = useRef(Date.now());
  const demoPrevTab       = useRef(null);
  const demoVisitedTabs   = useRef(new Set());
  // Analytics refs (authenticated app)
  const appSessionStart   = useRef(Date.now());
  const appTabStart       = useRef(Date.now());
  const appPrevTab        = useRef(null);
  // Derived: active player id & name
  // Priority: demo mode (URL param) > selectedDemo (dropdown) > own profile
  const activePlayerId   = (isDemoMode && !session) ? demoModePlayer?.id
    : selectedDemo ? selectedDemo.id : playerId;
  const activePlayerName = (isDemoMode && !session) ? demoModePlayer?.name
    : selectedDemo ? selectedDemo.name : playerName;
  // Auth state
  useEffect(() => {
    // No getSession() — onAuthStateChange fires INITIAL_SESSION on load which handles all cases
    // without the flash caused by getSession setting session before navigate completes
    // The handler awaits other supabase calls (subscription lookup), so it must
    // NOT run inside the onAuthStateChange callback itself: supabase-js invokes
    // that callback while holding its auth lock, and any supabase call awaited
    // in there needs the same lock -> permanent deadlock, INITIAL_SESSION never
    // resolves and the "Loading…" spinner stays forever (reproduced on browser
    // refresh with a stored session). Deferring via setTimeout runs it after
    // the lock is released.
    const handleAuthEvent = async (event, session) => {
      try {
        if (event === 'SIGNED_OUT') {
          setSession(null);
          window.location.replace('/');
          return;
        }
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          // Demo user — skip all subscription checks, go straight to app
          if (session.user.email === 'demo@zenanalytics.io') {
            setSession(session);
            return;
          }

          if (event === 'SIGNED_IN') {
            // Clear stale legacy keys from old tennis-era demo sessions
            localStorage.removeItem('zd_player_name');
            localStorage.removeItem('zd_player_id');
            // Kirjaa login-tapahtuma
            axios.post(`${API}/profiles/${session.user.id}/login-ping`, {}, {
              headers: { Authorization: `Bearer ${session.access_token}` },
            }).catch(e => console.error('[auth] login-ping failed:', e));
          }

          // On entry paths (root / register) always check subscription to avoid
          // flashing the main app before we know where to send the user.
          // Skip check when returning from Stripe checkout or portal.
          const sp = new URLSearchParams(window.location.search);
          const isStripeReturn = sp.get('checkout') === 'success' || sp.get('checkout') === 'trial' || sp.get('portal_return') === 'true';
          const onEntryPath = !isStripeReturn && (
            ['/', '/register', ''].includes(location.pathname) || window.location.hash === '#'
          );
          if (onEntryPath) {
            const subRes = await supabase.from('subscriptions').select('plan, status, source').eq('user_id', session.user.id).maybeSingle();

            const sub = subRes.data;

            // Pilot user — set plan and go straight to app
            if (!subRes.error && sub?.source === 'manual_pilot') {
              setUserPlan(sub.plan || null);
              setUserPlanStatus(sub.status || null);
              setUserSource('manual_pilot');
              setSession(session);
              return;
            }

            if (!subRes.error && !sub) {
              // Check if user just completed Stripe checkout — webhook may be delayed
              const checkoutTs = localStorage.getItem('zd_checkout_ts');
              const checkoutRecent = checkoutTs && (Date.now() - parseInt(checkoutTs, 10)) < 15 * 60 * 1000;
              if (checkoutRecent) {
                setShowWelcome(true);
                setSession(session);
                return;
              }
              // New user — no subscription → plan selection first, then profile
              navigate('/onboarding/plan');
              setSession(session);
              return;
            }
          }

          // Existing user with subscription (or already in app) → set session
          setSession(session);
          if (event === 'SIGNED_IN') {
            setActiveTab('home');
            window.scrollTo(0, 0);
          }
          return;
        }
        setSession(session);
      } catch (e) {
        console.error('[auth] onAuthStateChange error:', e);
        setSession(session ?? null); // always unblock spinner on error
      }
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(() => handleAuthEvent(event, session), 0);
    });
    return () => subscription.unsubscribe();
  }, []);
  // Fetch primary_sport when user logs in
  useEffect(() => {
    if (!session?.user?.id) return;
    // Reset on every session change (including a user switch without a full
    // page reload) so a previous user's resolved state can't let a stale
    // primarySport briefly leak into the next user's first render.
    setPrimarySportLoaded(false);
    const authHeaders = { headers: { Authorization: `Bearer ${session.access_token}` } };
    axios.get(`${API}/profiles/${session.user.id}/player-profile`, authHeaders)
      .then(r => {
        if (r.data?.primary_sport) setPrimarySport(r.data.primary_sport);
        const list = (r.data?.game_plan_sports || r.data?.primary_sport || '').split(',').filter(Boolean);
        setGamePlanSportsList(list);
        setDisplayLocale(r.data?.locale);
        if (!r.data?.timezone || !r.data?.locale) {
          axios.post(`${API}/profiles/player-profile`, {
            user_id: session.user.id,
            ...(!r.data?.timezone && { timezone: browserTimezone() }),
            ...(!r.data?.locale && { locale: browserLocale() }),
          }, authHeaders).catch(e => console.error('[App] timezone/locale default-fill failed:', e));
        }
      })
      .catch(e => console.error('[App] player-profile load failed:', e))
      // Settles the gate whether the fetch succeeded or failed — a network
      // hiccup must not leave sport-scoped tabs stuck on a loading screen
      // forever, it should just fall back to unscoped (same as today).
      .finally(() => setPrimarySportLoaded(true));
  }, [session]);
  // Sport switcher handlers — reuse the existing player-profile upsert
  // endpoint, no new backend work needed (see PRODUCT-GUIDELINES.md §5).
  const handleSwitchSport = (sport) => {
    if (!session?.user?.id || sport === primarySport) return;
    setPrimarySport(sport); // optimistic
    axios.post(`${API}/profiles/player-profile`, { user_id: session.user.id, primary_sport: sport }, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).catch(e => console.error('[handleSwitchSport] save failed:', e));
  };
  const handleAddSport = (sport) => {
    if (!session?.user?.id || gamePlanSportsList.includes(sport)) return;
    const next = [...gamePlanSportsList, sport];
    setGamePlanSportsList(next); // optimistic
    setPrimarySport(sport); // a newly-added sport becomes active — the player just asked to track it
    axios.post(`${API}/profiles/player-profile`, {
      user_id: session.user.id, game_plan_sports: next.join(','), primary_sport: sport,
    }, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).catch(e => console.error('[handleAddSport] save failed:', e));
  };
  // Fetch subscription plan for header badge + settings card
  useEffect(() => {
    if (!session?.user?.id) { setUserPlan(null); setUserPlanStatus(null); return; }

    const applySubData = (data) => {
      if (!data?.plan) return false;
      setUserPlan(data.plan);
      setUserPlanStatus(data.status || null);
      setUserSource(data.source || null);
      setUserPlanExpiresAt(data.current_period_end || null);
      if (data.payment_failed_at) setPaymentFailedAt(new Date(data.payment_failed_at));
      if (data.deletion_scheduled_at) setDeletionScheduledAt(new Date(data.deletion_scheduled_at));
      localStorage.removeItem('zd_checkout_ts');
      return true;
    };

    const fetchProfileFallback = () => {
      supabase.from('profiles')
        .select('plan')
        .eq('id', session.user.id)
        .maybeSingle()
        .then(({ data: pd }) => { setUserPlan(pd?.plan || null); })
        .catch(e => console.error('[fetchProfileFallback] plan load failed:', e));
    };

    // Try full select (requires payment_failed_at + deletion_scheduled_at columns)
    supabase.from('subscriptions')
      .select('plan, status, source, payment_failed_at, deletion_scheduled_at, current_period_end')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          // Columns may not exist yet — retry with base columns only
          supabase.from('subscriptions')
            .select('plan, status, source, current_period_end')
            .eq('user_id', session.user.id)
            .maybeSingle()
            .then(({ data: d2 }) => { if (!applySubData(d2)) fetchProfileFallback(); })
            .catch(() => fetchProfileFallback());
          return;
        }
        if (!applySubData(data)) fetchProfileFallback();
      })
      .catch(() => fetchProfileFallback());
  }, [session]);
  // Open Stripe Customer Portal for subscription management
  const [managePlanError, setManagePlanError] = useState(null);
  const handleManagePlan = async () => {
    if (!session?.user?.id) return;
    setManagePlanError(null);
    try {
      const res = await axios.post(`${API}/stripe/create-portal-session`, {
        user_id: session.user.id,
        return_url: window.location.origin + '/?portal_return=true',
      });
      window.location.href = res.data.url;
    } catch (e) {
      // No Stripe customer — trial or pilot user, send them to pricing
      if (e?.response?.status === 404) {
        window.location.href = '/?subscribe=true';
        return;
      }
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      setManagePlanError(`Stripe error: ${detail}`);
      setTimeout(() => setManagePlanError(null), 10000);
    }
  };
  // Fetch Alex Chen's profile if URL has ?demo=true (no login needed)
  useEffect(() => {
    if (!isDemoMode) return;
    axios.get(`${API}/game-plans/demo/player`)
      .then(r => {
        setDemoModePlayer(r.data);
        trackEvent('demo_start', { referrer: document.referrer || null });
      })
      .catch(err => { console.error('Demo player fetch failed:', err); setDemoLoadError(true); });
  }, [isDemoMode]);
  // Demo: track tab views + duration
  useEffect(() => {
    if (!isDemoMode) return;
    const prev = demoPrevTab.current;
    // Duration of previous tab
    if (prev !== null) {
      const elapsed = Math.round((Date.now() - demoTabStart.current) / 1000);
      trackEvent('demo_tab_duration', { tab: prev, seconds: elapsed });
    }
    // View of new tab
    demoVisitedTabs.current.add(activeTab);
    trackEvent('demo_tab_view', { tab: activeTab });
    demoPrevTab.current = activeTab;
    demoTabStart.current = Date.now();
  }, [activeTab, isDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Demo: track session end on page unload
  useEffect(() => {
    if (!isDemoMode) return;
    const handler = () => {
      trackEvent('demo_end', {
        total_seconds: Math.round((Date.now() - demoSessionStart.current) / 1000),
        tabs_visited: [...demoVisitedTabs.current],
      });
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDemoMode]);
  // App: track session start when user logs in
  useEffect(() => {
    if (!session || isDemoMode) return;
    appSessionStart.current = Date.now();
    trackEvent('app_session_start', { user_id: session.user.id });
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // App: track tab views + duration
  useEffect(() => {
    if (!session || isDemoMode) return;
    const prev = appPrevTab.current;
    if (prev !== null) {
      const elapsed = Math.round((Date.now() - appTabStart.current) / 1000);
      trackEvent('app_tab_duration', { tab: prev, seconds: elapsed, user_id: session.user.id });
    }
    trackEvent('app_tab_view', { tab: activeTab, user_id: session.user.id });
    appPrevTab.current = activeTab;
    appTabStart.current = Date.now();
  }, [activeTab, session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // App: track session end on page unload
  useEffect(() => {
    if (!session || isDemoMode) return;
    const uid = session.user.id;
    const handler = () => {
      trackEvent('app_session_end', {
        total_seconds: Math.round((Date.now() - appSessionStart.current) / 1000),
        user_id: uid,
      });
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [session?.user?.id, isDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const openHelp = () => {
    const map = { upload: 'upload', matches: 'matches', analytics: 'analytics', settings: 'settings' };
    setHelpSection(map[activeTab] || 'overview');
    setHelpOpen(true);
  };
  // Reset to home tab (+ scroll top) whenever entering demo mode or switching demo profile
  useEffect(() => {
    if (isDemoMode) {
      setActiveTab('home');
      window.scrollTo(0, 0);
    }
  }, [isDemoMode, demoProfileIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  // Land on Home when there is no saved player yet, skip in demo mode
  useEffect(() => {
    if (!playerId && !isDemoMode) setActiveTab('home');
  }, []);
  // Auth gate — blank screen while Supabase loads (skip in demo mode)
  if (session === undefined && !isDemoMode) return (
    <div style={{ minHeight: '100vh', background: '#0a0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #1e293b', borderTop: '3px solid #00d4ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: '#475569', fontSize: '13px' }}>Loading…</span>
      </div>
    </div>
  );
  // Demo mode without login — wait until demoModePlayer is loaded
  if (session === null && isDemoMode && !demoModePlayer) {
    if (demoLoadError) return (
      <div style={{ minHeight: '100vh', background: '#0a0f1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <span style={{ color: '#f87171', fontSize: '16px' }}>⚠️ Could not load demo — backend may be offline.</span>
        <a href="/" style={{ color: '#00d4ff', fontSize: '14px' }}>← Back to home</a>
      </div>
    );
    return (
      <div style={{ minHeight: '100vh', background: '#0a0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#00d4ff', fontSize: '16px' }}>Loading demo…</span>
      </div>
    );
  }
  // ── OAuth error callback — show clear message instead of blank screen ──────
  const oauthError = (() => {
    const sp = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const desc = sp.get('error_description') || hash.get('error_description');
    const code = sp.get('error_code') || hash.get('error_code');
    if (!desc && !code) return null;
    return { desc: decodeURIComponent(desc || '').replace(/\+/g, ' '), code };
  })();
  if (oauthError && session === null) {
    const isDbError = oauthError.desc?.toLowerCase().includes('database');
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0f1a',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Inter', system-ui, sans-serif",
        padding: '24px',
      }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>⚠️</div>
        <h2 style={{ color: '#f87171', fontSize: 20, fontWeight: 700, margin: '0 0 12px', textAlign: 'center' }}>
          Sign-in failed
        </h2>
        <p style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', maxWidth: 380, lineHeight: 1.7, margin: '0 0 8px' }}>
          {isDbError
            ? 'There was a problem setting up your account. This is a configuration issue on our end — not something you did wrong.'
            : oauthError.desc || 'An unexpected error occurred during sign-in.'}
        </p>
        {isDbError && (
          <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', maxWidth: 380, lineHeight: 1.6, margin: '0 0 28px' }}>
            Our team has been notified. Please try again in a moment or contact us at{' '}
            <a href="mailto:support@zenanalytics.io" style={{ color: '#00d4ff' }}>support@zenanalytics.io</a>.
          </p>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => { window.history.replaceState({}, '', '/register'); window.location.reload(); }}
            style={{
              background: '#0ea5e9', color: '#fff', border: 'none',
              borderRadius: 8, padding: '10px 22px', fontSize: 14,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Try again →
          </button>
          <a href="/" style={{
            background: 'transparent', color: '#94a3b8',
            border: '1px solid #334155', borderRadius: 8,
            padding: '10px 22px', fontSize: 14, fontWeight: 600,
            textDecoration: 'none',
          }}>
            ← Back to home
          </a>
        </div>
      </div>
    );
  }
  if (window.location.pathname === '/RAGadmin') {
    return <Suspense fallback={<RouteFallback />}><AdminApp /></Suspense>;
  }
  // Only auth-related hashes (Supabase OAuth callback) should bypass the public routes —
  // plain anchor hashes like #squash must still render the public pages.
  const isAuthHash = /access_token|refresh_token|error_code|type=recovery/.test(window.location.hash);
  if (session === null && !isDemoMode && !isAuthHash) {
    return (
      <>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/register" element={
          <RegisterPage
            onGoogleSignIn={triggerGoogleSignIn}
            onMicrosoftSignIn={triggerMicrosoftSignIn}
            onBack={() => navigate(-1)}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
          />
        } />
        <Route path="/terms" element={<TermsPage onBack={() => { navigate('/'); window.scrollTo(0, 0); }} onShowAbout={() => navigate('/about')} onShowTerms={() => navigate('/terms')} onShowPrivacy={() => navigate('/privacy')} />} />
        <Route path="/privacy" element={<PrivacyPage onBack={() => { navigate('/'); window.scrollTo(0, 0); }} onShowAbout={() => navigate('/about')} onShowTerms={() => navigate('/terms')} onShowPrivacy={() => navigate('/privacy')} />} />
        <Route path="/onboarding/profile" element={
          <ProfileSetupPage
            user={session?.user}
            onContinue={() => navigate('/')}
          />
        } />
        <Route path="/onboarding/plan" element={
          <PlanSelectPage
            user={session?.user}
            onBack={() => navigate('/')}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
            onTryDemo={() => navigate('/?demo=true')}
          />
        } />
        {/* Old marketing pages were removed; their URLs land on the front page until the new site is written */}
        <Route path="/features/*" element={<Navigate to="/" replace />} />
        <Route path="/features" element={<Navigate to="/" replace />} />
        <Route path="/sports" element={<Navigate to="/" replace />} />
        <Route path="/sports/:slug" element={
          <SportPage
            onGetStarted={() => navigate('/register')}
            onSignIn={() => navigate('/register')}
            onAbout={() => navigate('/about')}
            onShowAbout={() => navigate('/about')}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
            onPilotLogin={triggerGoogleSignIn}
          />
        } />
        <Route path="/reports" element={<Navigate to="/" replace />} />
        <Route path="/about" element={
          <AboutPage
            onBack={() => { navigate('/'); window.scrollTo(0, 0); }}
            onSignIn={triggerGoogleSignIn}
            onAbout={() => navigate('/about')}
            onShowContact={() => navigate('/contact')}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
          />
        } />
        <Route path="/contact" element={
          <ContactPage
            onBack={() => { navigate('/'); window.scrollTo(0, 0); }}
            onGetStarted={triggerGoogleSignIn}
            onShowContact={() => navigate('/contact')}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
          />
        } />
        {/* Etusivu RAG merged into /RAGadmin — old bookmarks still land somewhere useful */}
        <Route path="/taxonomy-admin" element={<Navigate to="/RAGadmin" replace />} />
        <Route path="/RAGadmin" element={<AdminApp />} />
        {/* Reporting Taxonomy page removed — it showcased a fabricated
            multi-sport report set (powerlifter/hiker/combat personas) that
            never matched the real product; the real 6 AI reports live on
            /features and /ai-analytics instead. */}
        <Route path="/reporting-taxonomy" element={<Navigate to="/" replace />} />
        <Route path="/not-authorized" element={<NotAuthorizedPage />} />
        <Route path="*" element={
          <LandingPage
            onGetStarted={() => navigate('/register')}
            onShowAbout={() => navigate('/about')}
            onAbout={() => navigate('/about')}
            onShowContact={() => navigate('/contact')}
            onShowTerms={() => navigate('/terms')}
            onShowPrivacy={() => navigate('/privacy')}
            onSignIn={() => navigate('/register')}
            onPilotLogin={triggerGoogleSignIn}
            onCheckout={async (plan) => {
              if (!session) {
                navigate('/register?plan=' + plan);
                return;
              }
              try {
                const res = await axios.post(`${API}/stripe/create-checkout-session`, {
                  plan,
                  user_id: session.user.id,
                  email: session.user.email,
                  success_url: window.location.origin + '/?checkout=success',
                  cancel_url: window.location.origin + '/?checkout=cancelled',
                }, { headers: { Authorization: `Bearer ${session.access_token}` } });
                window.location.href = res.data.url;
              } catch (e) {
                alert('Checkout failed. Please try again.');
              }
            }}
          />
        } />
      </Routes>
      </Suspense>
      <CookieBanner />
      </>
    );
  }
  if (session === null && !isDemoMode) return <LoginView />;

  // ── Onboarding routes — accessible when authenticated (new users) ──────────
  if (session !== null && location.pathname === '/onboarding/profile') {
    return <ProfileSetupPage user={session.user} onContinue={() => navigate('/')} />;
  }
  if (session !== null && location.pathname === '/onboarding/plan') {
    return (
      <PlanSelectPage
        user={session.user}
        onBack={() => navigate('/')}
        onShowTerms={() => navigate('/terms')}
        onShowPrivacy={() => navigate('/privacy')}
        onTryDemo={() => navigate('/?demo=true')}
      />
    );
  }

  const handlePlayerSaved = (id, name) => {
    if (id) {
      setPlayerId(id);
      localStorage.setItem('zd_player_id', id);
    }
    setPlayerName(name);
    localStorage.setItem('zd_player_name', name);
  };
  // ── Portal return confirmation screen ───────────────────────────────────────
  if (session && showPortalReturn) {
    return (
      <div style={{ minHeight: '100vh', background: '#060d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '24px' }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
          <h1 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px' }}>
            Subscription updated
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 32px', lineHeight: 1.6 }}>
            Your changes have been saved. It may take a moment for your plan to reflect in the app.
          </p>
          <button
            onClick={() => { setShowPortalReturn(false); navigate('/'); }}
            style={{ width: '100%', background: '#10b981', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '16px', fontWeight: 700, padding: '16px', cursor: 'pointer', marginBottom: '12px' }}
          >
            ← Back to home view
          </button>
          <p style={{ color: '#475569', fontSize: '12px', margin: 0 }}>
            Questions? <a href="mailto:support@zenanalytics.io" style={{ color: '#38bdf8' }}>support@zenanalytics.io</a>
          </p>
        </div>
      </div>
    );
  }

  // ── Pilot welcome — first login ─────────────────────────────────────────────
  if (session && showPilotWelcome === 'first') {
    const PLAN_LABELS = { insights: 'Insights', endurance: 'Endurance' };
    const planLabel = PLAN_LABELS[userPlan] || (userPlan ? userPlan.charAt(0).toUpperCase() + userPlan.slice(1) : 'Insights');
    return (
      <div style={{ minHeight: '100vh', background: '#060d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '24px' }}>
        <div style={{ maxWidth: '480px', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎁</div>
            <h1 style={{ color: '#f1f5f9', fontSize: '26px', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px' }}>
              Welcome!
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '15px', margin: '0 0 6px' }}>
              You've been granted free pilot access to
            </p>
            <p style={{ color: '#00d4ff', fontSize: '18px', fontWeight: 700, margin: 0 }}>
              {planLabel}
            </p>
          </div>

          {/* Step 1 */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: '#0d1829', border: '1px solid #1a2d47', borderRadius: '12px', padding: '18px 20px', marginBottom: '12px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#0ea5e9', color: '#fff', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>1</div>
            <div>
              <div style={{ color: '#f1f5f9', fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>Tell us about your game</div>
              <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>
                Your level, playing style and goals — so Data Coach knows who it is coaching. Takes about a minute.
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: '#0d1829', border: '1px solid #1a2d47', borderRadius: '12px', padding: '18px 20px', marginBottom: '28px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#10b981', color: '#fff', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>2</div>
            <div>
              <div style={{ color: '#f1f5f9', fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>Log your first Game Plan</div>
              <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>
                Plan a match or session, then log how it went. Data Coach learns from every plan and result.
              </div>
            </div>
          </div>

          <button
            onClick={() => { setShowPilotWelcome(null); navigate('/onboarding/profile'); }}
            style={{ width: '100%', background: '#00d4ff', border: 'none', borderRadius: '10px', color: '#060d1a', fontSize: '16px', fontWeight: 700, padding: '16px', cursor: 'pointer', marginBottom: '12px' }}
          >
            Get started →
          </button>
          <p style={{ color: '#475569', fontSize: '12px', marginTop: '4px', textAlign: 'center' }}>
            Step 1 takes about 1 minute.
          </p>
        </div>
      </div>
    );
  }

  // ── Pilot welcome — returning user ──────────────────────────────────────────
  if (session && showPilotWelcome === 'return') {
    return (
      <div style={{ minHeight: '100vh', background: '#060d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '24px' }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👋</div>
          <h1 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px' }}>
            Welcome back!
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 32px', lineHeight: 1.6 }}>
            Your pilot access is active. Let's pick up where you left off.
          </p>
          <button
            onClick={() => { setShowPilotWelcome(null); navigate('/app?tab=session_reports'); }}
            style={{ width: '100%', background: '#00d4ff', border: 'none', borderRadius: '10px', color: '#060d1a', fontSize: '16px', fontWeight: 700, padding: '16px', cursor: 'pointer' }}
          >
            Open ZenAnalytics →
          </button>
        </div>
      </div>
    );
  }

  // ── Checkout welcome screen ─────────────────────────────────────────────────
  if (session && showWelcome) {
    return (
      <div style={{ minHeight: '100vh', background: '#060d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif", padding: '24px' }}>
        <div style={{ maxWidth: '480px', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
            <h1 style={{ color: '#f1f5f9', fontSize: '26px', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px' }}>
              Welcome!
            </h1>
            <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
              Your subscription is active. Here's what happens next:
            </p>
          </div>

          {/* Step 1 */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: '#0d1829', border: '1px solid #1a2d47', borderRadius: '12px', padding: '18px 20px', marginBottom: '12px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#0ea5e9', color: '#fff', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>1</div>
            <div>
              <div style={{ color: '#f1f5f9', fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>Complete your profile</div>
              <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>
                Answer a few questions about your goals and training style so we can tailor your experience.
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: '#0d1829', border: '1px solid #1a2d47', borderRadius: '12px', padding: '18px 20px', marginBottom: '28px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#10b981', color: '#fff', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>2</div>
            <div>
              <div style={{ color: '#f1f5f9', fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>Log your first Game Plan</div>
              <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>
                Plan a match or session, then log how it went. Data Coach learns from every plan and result.
              </div>
            </div>
          </div>

          <button
            onClick={() => { setShowWelcome(false); navigate('/onboarding/profile'); }}
            style={{ width: '100%', background: '#0ea5e9', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '16px', fontWeight: 700, padding: '16px', cursor: 'pointer' }}
          >
            Aloita →
          </button>
          <p style={{ color: '#475569', fontSize: '12px', marginTop: '14px', textAlign: 'center' }}>
            14 päivän ilmainen kokeilu — ei maksukorttia tarvita tänään.
          </p>
        </div>
      </div>
    );
  }

  // Safety guard: never flash main app while navigating to onboarding routes
  if (location.pathname.startsWith('/onboarding')) {
    return <div style={{ minHeight: '100vh', background: '#060d1a' }} />;
  }


  // ── Subscription status helpers ───────────────────────────────────────────
  const now = new Date();
  const daysSinceFailure = paymentFailedAt ? Math.floor((now - paymentFailedAt) / 86400000) : null;
  const deletionDateStr = deletionScheduledAt
    ? deletionScheduledAt.toLocaleDateString(getDisplayLocale(), { day: 'numeric', month: 'numeric', year: 'numeric' })
    : null;
  const isAccessRestricted = userPlanStatus === 'access_restricted' || (daysSinceFailure !== null && daysSinceFailure >= 3);
  const showPaymentBanner      = userPlanStatus === 'past_due' && daysSinceFailure !== null && daysSinceFailure < 3;
  const showCancellationBanner = userPlanStatus === 'canceled' && !!deletionScheduledAt;

  // Locked UI — päivät 3-35: vain maksutavan päivitys mahdollista
  if (session && !isDemoMode && isAccessRestricted && deletionScheduledAt) {
    return (
      <div style={{ minHeight: '100vh', background: '#060d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', padding: '24px' }}>
        <div style={{ maxWidth: '480px', width: '100%', background: '#0d1829', border: '1px solid #1a2d47', borderRadius: '16px', padding: '40px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ color: '#f1f5f9', fontSize: '22px', fontWeight: 800, margin: '0 0 12px' }}>Tilisi on rajoitettu</h2>
          <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6, margin: '0 0 8px' }}>
            Maksusi ei mennyt läpi. Päivitä maksutapasi jatkaaksesi ZenAnalyticsin käyttöä.
          </p>
          {deletionDateStr && (
            <p style={{ color: '#f87171', fontSize: '13px', fontWeight: 600, margin: '0 0 24px' }}>
              ⚠️ Tilisi ja kaikki tietosi poistetaan automaattisesti {deletionDateStr}, jos maksua ei suoriteta.
            </p>
          )}
          <button
            onClick={handleManagePlan}
            style={{ width: '100%', background: '#0ea5e9', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '15px', fontWeight: 700, padding: '14px', cursor: 'pointer' }}
          >
            Päivitä maksutapa →
          </button>
          <button
            onClick={async () => { try { await supabase.auth.signOut(); } catch (_) {} setSession(null); window.location.href = '/'; }}
            style={{ marginTop: '12px', background: 'none', border: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer' }}
          >
            Kirjaudu ulos
          </button>
        </div>
      </div>
    );
  }

  // True for both ?demo=true visitors AND logged-in demo@zenanalytics.io users
  const isDemoAccount = isDemoMode || session?.user?.email === 'demo@zenanalytics.io';

  return (
    <div style={{ minHeight: '100vh', background: activeTab === 'settings' ? 'transparent' : '#0f172a', color: '#e2e8f0', fontFamily: 'sans-serif', paddingBottom: '70px', overflowX: 'hidden' }}>

      {/* Header */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', position: 'sticky', top: 0, zIndex: 50 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 20px', maxWidth: '1100px', margin: '0 auto' }}>
        <button
          onClick={() => switchTab('home')}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, flex: 1, minWidth: 0, textAlign: 'left' }}
          title="Back to Home"
        >
          <ZenLogo size={32} />
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, color: '#38bdf8', fontSize: '20px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>ZenAnalytics</h1>
            <p style={{ margin: 0, color: '#cbd5e1', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Performance analytics system</p>
          </div>
        </button>
        {/* Plan badge — logged-in users only */}
        {session && !isDemoMode && userPlan && (() => {
          const PM = {
            insights:       { label: 'Insights',        color: '#c8854a', bg: 'rgba(200,133,74,0.12)',  border: 'rgba(200,133,74,0.35)'  },
            endurance:      { label: 'Endurance',       color: '#3dbfb0', bg: 'rgba(61,191,176,0.12)',  border: 'rgba(61,191,176,0.35)'  },
            pilot:          { label: '🎁 Pilot',        color: '#34d399', bg: 'rgba(52,211,153,0.10)',  border: 'rgba(52,211,153,0.35)'  },
            pilot_data_coach: { label: '🎁 Data Coach',     color: '#a78bfa', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.35)' },
          };
          const m = PM[userPlan];
          if (!m) return null;
          return (
            <div style={{
              color: m.color, background: m.bg, border: `1px solid ${m.border}`,
              borderRadius: '20px', padding: '3px 11px',
              fontSize: '11px', fontWeight: '700', letterSpacing: '0.07em',
              textTransform: 'uppercase', flexShrink: 0, cursor: 'default',
            }}>
              {m.label}
            </div>
          );
        })()}
        {isDemoMode && (
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: '8px',
              color: '#e2e8f0',
              fontSize: '13px',
              fontWeight: '600',
              padding: '7px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'background 0.15s, border-color 0.15s',
              fontFamily: 'inherit',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.13)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.40)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
          >
            <span className="demo-back-full">← Back to front page</span>
            <span className="demo-back-short">← Back</span>
          </button>
        )}
        {session && !isDemoMode && (
          <SportSwitcher
            sports={gamePlanSportsList}
            activeSport={primarySport}
            onSwitch={handleSwitchSport}
          />
        )}
        {session && !isDemoMode && <FeedbackWidget session={session} activeTab={activeTab} openTrigger={feedbackOpenTrigger} />}
        <InstallPWA />
        <UserDropdown
          playerName={playerName}
          demoPlayers={demoPlayers}
          selectedDemo={selectedDemo}
          onSelectDemo={setSelectedDemo}
          onSignOut={async () => {
            try { await supabase.auth.signOut(); } catch (_) {}
            setSession(null);
            window.location.href = '/';
          }}
          isDemoGuest={(isDemoMode && !session) || session?.user?.email === 'demo@zenanalytics.io'}
          onSignIn={triggerGoogleSignIn}
          onManagePlan={handleManagePlan}
          onGoToProfile={() => switchTab('settings')}
          onGiveFeedback={session && !isDemoMode ? () => setFeedbackOpenTrigger(t => t + 1) : null}
          userPlan={userPlan}
          userPlanStatus={userPlanStatus}
        />
        {/* Help button hidden until manual is updated */}
      </div>{/* /inner header content */}
      </div>{/* /header */}

      {/* Demo profile banner */}
      {session?.user?.email === 'demo@zenanalytics.io' && (
        <div style={{
          background: 'linear-gradient(90deg, rgba(251,146,60,0.12) 0%, rgba(251,146,60,0.06) 100%)',
          borderBottom: '1px solid rgba(251,146,60,0.25)',
          padding: '8px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>👁</span>
              <span style={{ color: '#fb923c', fontSize: 13, fontWeight: 600 }}>You are viewing a demo profile</span>
            </div>
            <span style={{ color: 'rgba(251,146,60,0.7)', fontSize: 12, paddingLeft: 22 }}>Read-only demo — create an account to log your own matches and chat with Data Coach.</span>
          </div>
          <a href="/" style={{
            color: '#00d4ff', fontSize: 12, fontWeight: 600,
            textDecoration: 'none', border: '1px solid rgba(0,212,255,0.3)',
            borderRadius: 6, padding: '3px 10px',
            background: 'rgba(0,212,255,0.08)',
          }}>
            Create your account →
          </a>
        </div>
      )}

      {/* Manage plan error toast */}
      {managePlanError && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: '#1e1b2e', border: '1px solid rgba(239,68,68,0.5)',
          borderRadius: '10px', padding: '14px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          maxWidth: '320px', animation: 'fadeInUp 0.2s ease',
        }}>
          <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
          <span style={{ color: '#fca5a5', fontSize: '13px', lineHeight: '1.5' }}>{managePlanError}</span>
        </div>
      )}
      {/* Cancellation banner — vapaaehtoinen peruutus */}
      {showCancellationBanner && deletionDateStr && (
        <div style={{
          background: 'rgba(245,158,11,0.12)', borderBottom: '1px solid rgba(245,158,11,0.35)',
          padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
        }}>
          <span style={{ color: '#fcd34d', fontSize: '13px' }}>
            Tilauksesi on peruutettu. Tietosi poistetaan automaattisesti {deletionDateStr} — reaktivoi tilaus säilyttääksesi tietosi.
          </span>
          <button
            onClick={handleManagePlan}
            style={{ background: '#f59e0b', border: 'none', borderRadius: '7px', color: '#fff', fontSize: '12px', fontWeight: 700, padding: '7px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Reaktivoi →
          </button>
        </div>
      )}
      {/* Payment failed banner — päivät 0-2 */}
      {showPaymentBanner && deletionDateStr && (
        <div style={{
          background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.35)',
          padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
        }}>
          <span style={{ color: '#fca5a5', fontSize: '13px' }}>
            ⚠️ Maksusi ei mennyt läpi. Päivitä maksutapa pitääksesi tilisi. Tili ja kaikki tiedot poistetaan {deletionDateStr}, jos maksua ei suoriteta.
          </span>
          <button
            onClick={handleManagePlan}
            style={{ background: '#ef4444', border: 'none', borderRadius: '7px', color: '#fff', fontSize: '12px', fontWeight: 700, padding: '7px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Päivitä →
          </button>
        </div>
      )}
      {/* Checkout success banner */}
      {checkoutSuccess && (
        <div style={{
          background: 'rgba(34,197,94,0.12)', borderBottom: '1px solid rgba(34,197,94,0.35)',
          padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🎉</span>
            <div>
              <span style={{ color: '#4ade80', fontWeight: 700, fontSize: '14px' }}>Subscription activated!</span>
              <span style={{ color: '#86efac', fontSize: '13px', marginLeft: '8px' }}>Welcome aboard — your plan is now active.</span>
            </div>
          </div>
          <button onClick={() => setCheckoutSuccess(false)} style={{ background: 'none', border: 'none', color: '#86efac', fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
      )}
      {/* Demo banner — URL ?demo=true (no login) */}
      {isDemoMode && !session && demoModePlayer && (
        <div style={{
          background: 'rgba(6,13,26,0.97)', borderBottom: '1px solid #1a3050',
          padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        }}>
          {/* ZenHeadCoach athletes */}
          <span style={{ fontSize: '10px', color: '#475569', whiteSpace: 'nowrap', marginRight: '2px' }}>
            🏆
          </span>
          {DEMO_ATHLETES.filter(a => a.tier === 'headcoach').map((a, i) => {
            const idx = DEMO_ATHLETES.indexOf(a);
            return (
              <button
                key={a.key}
                onClick={() => setDemoProfileIdx(idx)}
                title={`${a.name}, ${a.age} — ${a.goal}`}
                style={{
                  background: demoProfileIdx === idx ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${demoProfileIdx === idx ? '#00d4ff55' : '#1a3050'}`,
                  borderRadius: '20px', padding: '4px 11px', cursor: 'pointer',
                  color: demoProfileIdx === idx ? '#00d4ff' : '#94a3b8',
                  fontSize: '12px', fontWeight: demoProfileIdx === idx ? 600 : 400,
                  fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s',
                }}
              >
                {a.flag} {a.name.split(' ')[0]}
              </button>
            );
          })}
          {/* Divider */}
          <span style={{ width: '1px', height: '18px', background: '#1a3050', margin: '0 4px', flexShrink: 0 }} />
          {/* Data Coach lifestyle professionals */}
          <span style={{ fontSize: '10px', color: '#475569', whiteSpace: 'nowrap', marginRight: '2px' }}>
            💼
          </span>
          {DEMO_ATHLETES.filter(a => a.tier === 'coach').map((a) => {
            const idx = DEMO_ATHLETES.indexOf(a);
            return (
              <button
                key={a.key}
                onClick={() => setDemoProfileIdx(idx)}
                title={`${a.name}, ${a.age} — ${a.goal}`}
                style={{
                  background: demoProfileIdx === idx ? 'rgba(168,85,247,0.14)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${demoProfileIdx === idx ? '#a855f755' : '#1a3050'}`,
                  borderRadius: '20px', padding: '4px 11px', cursor: 'pointer',
                  color: demoProfileIdx === idx ? '#c084fc' : '#94a3b8',
                  fontSize: '12px', fontWeight: demoProfileIdx === idx ? 600 : 400,
                  fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s',
                }}
              >
                {a.flag} {a.name.split(' ')[0]}
              </button>
            );
          })}
          <span style={{ fontSize: '11px', color: '#475569', marginLeft: 'auto', display: 'none' }}>
            {DEMO_ATHLETES[demoProfileIdx]?.goal}
          </span>
        </div>
      )}
      {/* Demo banner — dropdown selected demo player (logged in) */}
      {selectedDemo && (
        <div style={{
          background: 'rgba(234,88,12,0.15)', borderBottom: '1px solid rgba(234,88,12,0.3)',
          padding: '8px 20px', display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '13px', color: '#fb923c' }}>
            👁 Viewing demo data: <strong>{selectedDemo.name}</strong>
          </span>
          <button onClick={() => setSelectedDemo(null)} style={{
            marginLeft: 'auto', background: 'none',
            border: '1px solid rgba(234,88,12,0.4)', borderRadius: '6px',
            color: '#fb923c', fontSize: '12px', cursor: 'pointer', padding: '3px 10px',
          }}>Switch back to your profile</button>
        </div>
      )}
      {/* Content */}
      {/* activeSport (primarySport) race fix, 2026-10-28: the three
          sport-scoped tabs below wait for primarySportLoaded before
          mounting, instead of mounting on `session` alone and passing
          activeSport={null} for one render — see primarySportLoaded's
          declaration for why that mattered. This placeholder is what
          fills that (now very brief) gap instead of a blank tab. */}
      {['chat', 'program', 'home'].includes(activeTab) && session && !primarySportLoaded && (
        <div style={{ padding: 40, textAlign: 'center', color: '#8b949e', fontFamily: 'sans-serif' }}>Loading…</div>
      )}
      {/* ── Data Coach tabs ── */}
      {activeTab === 'chat' && session && !isDemoMode && primarySportLoaded && (
        <DataCoachApp user={session.user} activeSport={primarySport} />
      )}
      {activeTab === 'chat' && isDemoMode && (
        <DemoChatView athleteKey={DEMO_ATHLETES[demoProfileIdx]?.key} />
      )}
      {activeTab === 'program' && session && !isDemoAccount && primarySportLoaded && (
        <TabErrorBoundary>
          <GamePlanTab
            session={session}
            activeSport={primarySport}
            onNavigateToAnalysis={() => { switchTab('reports'); setDataLabView('calendar'); }}
            pendingOpenPlan={pendingOpenPlan}
            onPendingOpenPlanConsumed={() => setPendingOpenPlan(null)}
            pendingOpenCompetition={pendingOpenCompetition}
            onPendingOpenCompetitionConsumed={() => setPendingOpenCompetition(null)}
            closeModalTrigger={tutorialModalCloseTrigger}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'program' && isDemoAccount && session && primarySportLoaded && (
        <TabErrorBoundary>
          <GamePlanTab
            session={session}
            activeSport={primarySport}
            onNavigateToAnalysis={() => { switchTab('reports'); setDataLabView('calendar'); }}
            pendingOpenPlan={pendingOpenPlan}
            onPendingOpenPlanConsumed={() => setPendingOpenPlan(null)}
            pendingOpenCompetition={pendingOpenCompetition}
            onPendingOpenCompetitionConsumed={() => setPendingOpenCompetition(null)}
            readOnly={true}
            closeModalTrigger={tutorialModalCloseTrigger}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'program' && isDemoMode && !session && (
        <TabErrorBoundary>
          <GamePlanTab
            session={null}
            demoMode={true}
            readOnly={true}
            onNavigateToAnalysis={() => { switchTab('reports'); setDataLabView('calendar'); }}
            pendingOpenPlan={pendingOpenPlan}
            onPendingOpenPlanConsumed={() => setPendingOpenPlan(null)}
            pendingOpenCompetition={pendingOpenCompetition}
            onPendingOpenCompetitionConsumed={() => setPendingOpenCompetition(null)}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'session_reports' && session && (
        <TabErrorBoundary>
          <GamePlanView
            session={session}
            readOnly={isDemoAccount}
            activeSport={primarySport}
            onOpenPlan={(plan) => { setPendingOpenPlan(plan); switchTab('program'); }}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'session_reports' && isDemoMode && !session && (
        <TabErrorBoundary>
          <GamePlanView
            session={null}
            demoMode={true}
            readOnly={true}
            onOpenPlan={(plan) => { setPendingOpenPlan(plan); switchTab('program'); }}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'home' && session && primarySportLoaded && (
        <TabErrorBoundary>
          <HomeView
            session={session}
            activeSport={primarySport}
            onOpenPlan={(plan) => { setPendingOpenPlan(plan); switchTab('program'); }}
            onOpenCompetition={(comp) => { setPendingOpenCompetition(comp); switchTab('program'); }}
            onAddSession={() => switchTab('program')}
            onGoToCoach={() => switchTab('chat')}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'home' && isDemoMode && !session && (
        <TabErrorBoundary>
          <HomeView
            session={null}
            demoMode={true}
            onOpenPlan={(plan) => { setPendingOpenPlan(plan); switchTab('program'); }}
            onAddSession={() => switchTab('program')}
            onGoToCoach={() => switchTab('chat')}
          />
        </TabErrorBoundary>
      )}
      {activeTab === 'settings' && (
        <SettingsView
          playerName={playerName}
          playerId={playerId}
          onPlayerSaved={handlePlayerSaved}
          session={session}
          isDemoMode={isDemoAccount}
          demoAthlete={isDemoAccount ? (DEMO_ATHLETES[demoProfileIdx] || { name: 'Alex Johnsson', sport: 'tennis', age: 32, gender: 'male', level: 'Club player', goals: ['Improve match consistency', 'Better recovery tracking'] }) : undefined}
          userPlan={userPlan}
          userPlanStatus={userPlanStatus}
          userPlanExpiresAt={userPlanExpiresAt}
          onManagePlan={handleManagePlan}
          managePlanError={managePlanError}
          gamePlanSportsList={gamePlanSportsList}
          onAddSport={handleAddSport}
        />
      )}
      {/* Help Modal */}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} initialSection={helpSection} />}
      {/* Bottom Nav */}
      <BottomNav
        active={activeTab}
        onChange={switchTab}
        hideTabs={['upload', 'matches']}
      />
      {/* Floating Feedback Button */}
      {session && <FloatingFeedback session={session} />}
      {/* GDPR Cookie Banner */}
      <CookieBanner />
      {/* Themed confirm() replacement — mounted once here, used anywhere via confirmDialog() */}
      <ConfirmDialogHost />
      {/* Tutorial Overlay — uusille kävijöille ja tour-linkistä tuleville */}
      <TutorialOverlay
        onTabChange={switchTab}
        activeTab={activeTab}
        onDone={() => {}}
        isDemoMode={isDemoMode}
        onOpenSession={async (date, tab) => {
          const userId = session?.user?.id;
          if (!userId) return;
          try {
            let q = supabase
              .from('session_plans')
              .select('*')
              .eq('user_id', userId)
              .is('deleted_at', null)
              .eq('scheduled_date', date);
            if (primarySport) q = q.eq('sport', primarySport);
            const { data } = await q.limit(1).single();
            if (data) {
              switchTab('program');
              setPendingOpenPlan({ ...data, _initialTab: tab });
            }
          } catch (_) {}
        }}
        onCloseSession={() => setTutorialModalCloseTrigger(t => t + 1)}
      />
    </div>
  );
}
/* ─── Floating Feedback ──────────────────────────────────────────── */
function FloatingFeedback({ session }) {
  const [open, setOpen]         = useState(false);
  const [rating, setRating]     = useState(0);
  const [thought, setThought]   = useState('');
  const [sent, setSent]         = useState(false);
  const [sending, setSending]   = useState(false);
  const [hovered, setHovered]   = useState(0);
  const handleSend = async () => {
    if (!rating) return;
    setSending(true);
    try {
      await supabase.from('feedback').insert([{
        user_id:  session?.user?.id || null,
        rating,
        most_useful: thought || null,
        source: 'in_app',
      }]);
      setSent(true);
      setTimeout(() => { setOpen(false); setSent(false); setRating(0); setThought(''); }, 2000);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };
  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: '80px', right: '16px', zIndex: 500,
          background: '#1e293b', border: '1px solid #334155',
          borderRadius: '20px', padding: '8px 14px',
          color: '#cbd5e1', fontSize: '13px', fontWeight: '600',
          cursor: 'pointer', alignItems: 'center', gap: '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          display: 'none', /* hidden — re-enable when ready */
        }}
      >💬 Feedback</button>
      {/* Modal */}
      {open && (
        <div style={{
          position: 'fixed', bottom: '130px', right: '16px', zIndex: 600,
          background: '#0f172a', border: '1px solid #1e293b',
          borderRadius: '16px', padding: '20px',
          width: '280px', boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        }}>
          {sent ? (
            <p style={{ color: '#4ade80', textAlign: 'center', fontWeight: '700', margin: 0 }}>
              ✓ Thanks for the feedback!
            </p>
          ) : (
            <>
              <p style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '15px', margin: '0 0 14px' }}>
                What do you think?
              </p>
              {/* Stars */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHovered(n)}
                    onMouseLeave={() => setHovered(0)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '24px', padding: 0, lineHeight: 1,
                      color: n <= (hovered || rating) ? '#facc15' : '#1e293b',
                    }}
                  >★</button>
                ))}
              </div>
              {/* Textarea */}
              <textarea
                rows={3}
                value={thought}
                onChange={e => setThought(e.target.value)}
                placeholder="Any thoughts? (optional)"
                style={{
                  width: '100%', background: '#0a0f1a',
                  border: '1px solid #1e293b', borderRadius: '8px',
                  padding: '10px 12px', color: '#e2e8f0',
                  fontSize: '13px', fontFamily: 'inherit',
                  resize: 'none', outline: 'none', boxSizing: 'border-box',
                  marginBottom: '12px',
                }}
              />
              <button
                onClick={handleSend}
                disabled={!rating || sending}
                style={{
                  width: '100%', background: rating ? '#00d4ff' : '#1e293b',
                  border: 'none', borderRadius: '8px', padding: '10px',
                  color: rating ? '#0a0f1a' : '#475569',
                  fontWeight: '700', fontSize: '14px',
                  cursor: rating ? 'pointer' : 'not-allowed',
                  transition: 'all 0.15s',
                }}
              >{sending ? 'Sending…' : 'Send →'}</button>
            </>
          )}
        </div>
      )}
    </>
  );
}
